
import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MarketingTags from '@/components/MarketingTags';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarWithPrices, CalendarRangeStyles } from '@/components/ui/CalendarWithPrices';
import CalendarDock from '@/components/ui/CalendarDock';

/** First selectable departure day (22 days out) — calendars open here instead of a fully-disabled current month. */
const getMinBookableDate = () => new Date(new Date().setDate(new Date().getDate() + 22));

import AllInPriceToggle from '@/components/ui/AllInPriceToggle';
import { useAllInPrices } from '@/hooks/useAllInPrices';
import { useFlightCalendarPrices } from '@/hooks/useFlightCalendarPrices';
import { format, parse } from 'date-fns';
import { Calendar as CalendarIcon, Search as SearchIcon, Users, MapPin, X, Sparkles, Plane } from 'lucide-react';
import { streamGoogleFlights, searchGoogleFlightsWithToken } from '@/api/functions';
import { buildFlightSearchParams, flightSearchKey, prefetchFlightSearch, takePrefetchedFlightSearch } from '@/lib/searchPrefetch';
import { initialSearchTravelers, rememberSearchTravelers } from '@/lib/usualTravelers';
import { useSearchPickers } from '@/hooks/useSearchPickers';
import { checkBuildWhileSearching, loadNewBuildForSearch, resultsPageUrl } from '@/lib/searchNavigation';
import AirportInput from '../components/search/AirportInput';
import PassengerSelector from '../components/search/PassengerSelector';
import FlightOfferCard from '../components/search/FlightOfferCard';
import CheaperAlternatives from '../components/search/CheaperAlternatives';
import CabinClassSelector from '../components/search/CabinClassSelector';
import PaymentMethodSelector from '../components/search/PaymentMethodSelector';
import FilterSort from '../components/search/FilterSort';
import SearchSpeedStat from '../components/search/SearchSpeedStat';
import RoyalAmbientBackground from '@/components/ui/RoyalAmbientBackground';
import { useNavigate, useLocation } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { toast } from 'sonner';
import { generateSearchActivity } from '@/data/searchActivityData';
import { isNativeApp } from '@/native/bootstrap';
import { deferaCapitalUrl, deferaCapitalNmiUrl, deferaZeroUrl } from '@/lib/apiUrls';
import { mergeSupabaseEdgeHeaders } from '@/lib/reserveActAs';
import { supabase } from '@/lib/supabase';
import { normalizeCabinClass } from '@/utils/cabinClass';
import { readPassengerParams } from '@/utils/passengerTypes';
import { nextSearchDateRange, nextChangeFlightDateRange } from '@/utils/searchDateRange';

const DEFERA_ZERO_ALLOWED_AIRLINES = ['united', 'american', 'southwest', 'jetblue', 'virgin'];

/** A flight with no booking token has no fare options, so no price we can stand behind. */
const UNBOOKABLE_FLIGHT_MESSAGE = 'This flight can’t be booked right now. Please choose another flight.';

/**
 * Whether a change-flight search hides Basic Economy: only once the ticket
 * is booked (an airline confirmation is on file) and the booked fare isn't
 * already Basic — Basic would be a downgrade. Before booking, the customer
 * can freely pick any fare brand, so everything shows. The search and the
 * early search both ask this, so they always request the same flights.
 */
async function changeSearchHidesBasic(changeFlightTrip) {
  const { getMarketingFareName } = await import('@/utils/marketingFareName');
  const { tripHasAirlineConfirmation } = await import('@/utils/changeFlightEligibility');
  const fareName = (getMarketingFareName(changeFlightTrip[0]) || '').toLowerCase();
  return tripHasAirlineConfirmation(changeFlightTrip) && !fareName.includes('basic');
}

// Helper to parse ISO 8601 duration string to minutes
const parseDuration = (isoDuration) => {
  if (!isoDuration) return Infinity;
  const matches = isoDuration.match(/PT(\d+H)?(\d+M)?/);
  if (!matches) return Infinity;
  const hours = matches[1] ? parseInt(matches[1].slice(0, -1)) : 0;
  const minutes = matches[2] ? parseInt(matches[2].slice(0, -1)) : 0;
  return hours * 60 + minutes;
};

/** Total outbound duration in minutes, for the Duration sort — sums
 *  segment durations plus layovers (mirrors FlightOfferCard's display
 *  math so the sort order matches what the customer reads on the
 *  cards), falling back to the wall-clock departure→arrival span when
 *  segment durations are missing. Returns MAX_SAFE_INTEGER for
 *  incomparable offers so they sink to the bottom instead of NaN-ing
 *  the comparator. */
const getOutboundDurationMinutes = (offer) => {
  const segs = offer?.slices?.[0]?.segments;
  if (!Array.isArray(segs) || segs.length === 0) return Number.MAX_SAFE_INTEGER;
  let total = 0;
  for (const seg of segs) {
    const mins = parseDuration(seg?.duration);
    if (Number.isFinite(mins)) total += mins;
    const layover = Number(seg?.layover_duration);
    if (Number.isFinite(layover) && layover > 0) total += layover;
  }
  if (total > 0) return total;
  const dep = new Date(segs[0]?.departing_at).getTime();
  const arr = new Date(segs[segs.length - 1]?.arriving_at).getTime();
  if (Number.isFinite(dep) && Number.isFinite(arr) && arr > dep) {
    return (arr - dep) / 60_000;
  }
  return Number.MAX_SAFE_INTEGER;
};

/** Outbound departure timestamp (ms) for the Departure sort. Same
 *  MAX_SAFE_INTEGER sink for offers with no parseable time. */
const getOutboundDepartureMs = (offer) => {
  const ms = new Date(offer?.slices?.[0]?.segments?.[0]?.departing_at).getTime();
  return Number.isFinite(ms) ? ms : Number.MAX_SAFE_INTEGER;
};

export default function SearchPage() {
  // Shopping price display: all-in totals (default) vs base fares; persists
  // through booking options via localStorage (useAllInPrices).
  const [showAllInPrices, setShowAllInPrices] = useAllInPrices();
  const navigate = useNavigate();
  const location = useLocation();
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  
  // Save origin to localStorage whenever it changes
  useEffect(() => {
    if (origin && origin.trim()) {
      localStorage.setItem('lastSearchOrigin', origin.trim());
    }
  }, [origin]);
  const [date, setDate] = useState({
    from: undefined,
    to: undefined
  });
  // A search in the URL brings its own travelers; an empty form opens at the
  // party this customer usually searches for (usualTravelers.js).
  const [passengers, setPassengers] = useState(() => {
    const query = new URLSearchParams(location.search);
    return query.get('origin') ? readPassengerParams(query) : initialSearchTravelers();
  });
  const [cabinClass, setCabinClass] = useState('economy');
  const [paymentMethod, setPaymentMethod] = useState('installments');
  const [milesBalance, setMilesBalance] = useState(0);
  const [reserveBalanceCents, setReserveBalanceCents] = useState(0);
  const [offers, setOffers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  /** The running search, so a newer one (or leaving the page) can cancel it. */
  const searchRunRef = useRef({ id: 0, controller: null });
  /** The search last started by Search (flightSearchKey), so the form's
   *  early search never re-runs the one already on screen. */
  const lastSearchKeyRef = useRef(null);
  const [error, setError] = useState(null);
  const [numberOfMonths, setNumberOfMonths] = useState(2);
  const [allOffers, setAllOffers] = useState([]); // Stores all raw offers from API
  // This search's speed for the stat over the results: press-to-first-flights
  // time, and whether every flight is in yet (SearchSpeedStat).
  const [searchSpeed, setSearchSpeed] = useState(null);
  const itemsPerPage = 50;
  const [visibleCount, setVisibleCount] = useState(itemsPerPage);
  /** Return-leg Show-more window — independent of the outbound one so
   *  expanding one list never expands the other. Reset on every new
   *  return-leg fetch. */
  const [returnVisibleCount, setReturnVisibleCount] = useState(itemsPerPage);
  const [hasSearched, setHasSearched] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const { isTravelersOpen, onTravelersOpenChange, earlySearchDelay } = useSearchPickers({ isCalendarOpen });
  const [returnFlightOffers, setReturnFlightOffers] = useState([]);
  const [selectedOutboundFlight, setSelectedOutboundFlight] = useState(null);
  const [showReturnFlights, setShowReturnFlights] = useState(false);
  const [originalSearchParams, setOriginalSearchParams] = useState(null);
  /** `cheaper_alternatives` from the last successful search response
   *  — populated by handleSearch from SearchAPI's field on the primary
   *  /api/defera-flights/search payload. Google withholds this field
   *  on most routes, so the empty-list common case is why the
   *  CheaperAlternatives callout renders null on many searches. */
  const [cheaperAlternatives, setCheaperAlternatives] = useState([]);
  /** Whether the customer has opened the "Change dates" strip inside
   *  the CheaperAlternatives callout. Gates the strip's calendar-price
   *  fetch so we don't spend a SearchAPI call unless they actually
   *  want to shop dates. Reset whenever a fresh search runs. */
  const [dateStripOpen, setDateStripOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  // 'best' (default — Google's ranked picks pinned above a price-sorted
  // list), 'price', 'duration', 'departure'. Explicit sorts render one
  // flat sorted list; only 'best' shows the Best/Other sections.
  const [sortBy, setSortBy] = useState('best');
  const [stopsFilter, setStopsFilter] = useState('any'); // 'any', '0', '1'
  const [airlineFilter, setAirlineFilter] = useState('all');
  const [autoSearchTriggered, setAutoSearchTriggered] = useState(false);
  const [tripMode, setTripMode] = useState('flight'); // 'flight' or 'flight_stay'

  /** Currently-visible month in the date-picker (UTC midnight, first
   *  of the month). Drives the SearchAPI calendar-prices fetch so we
   *  only ask for prices in the window the customer is actually
   *  looking at. Bookings must be 22+ days out, so anchor to
   *  today+22 by default. */
  const [calendarViewMonth, setCalendarViewMonth] = useState(() => {
    const anchor = new Date();
    anchor.setDate(anchor.getDate() + 22);
    return new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), 1));
  });

  /** Cheapest one-way price per outbound date — hydrated by
   *  SearchAPI's `google_flights_calendar` engine via
   *  `/api/flight-calendar-prices`. Renders a "$X" hint under each
   *  day in the date-picker; only fires when the picker is open AND
   *  both airports are set, so we don't spend calls on empty forms. */
  const {
    prices: calendarPrices,
    lowestPrice: calendarLowestPrice,
    isLoading: calendarPricesLoading,
  } = useFlightCalendarPrices({
    origin,
    destination,
    startDate: calendarViewMonth,
    days: 62,
    cabinClass,
    passengers,
    enabled: isCalendarOpen,
    // Always show round-trip prices on the calendar — matches Google
    // Flights' default UX where the date grid assumes a return trip
    // even before the customer has picked one. Trip length uses the
    // customer's actual range when both dates are picked, otherwise
    // falls back to Google's 7-day default so a blank / half-drawn
    // range still hints at realistic round-trip pricing.
    flightType: 'round_trip',
    tripLengthDays: (date?.from instanceof Date && date?.to instanceof Date)
      ? Math.max(1, Math.round((date.to.getTime() - date.from.getTime()) / 86_400_000))
      : 7,
  });

  /** Trip length of the LAST-searched range, used to derive each
   *  strip cell's return date and to decide round-trip vs one-way.
   *  Falls back to 0 (one-way) when there's no return date. */
  const searchTripLengthDays = useMemo(() => {
    if (date?.from instanceof Date && date?.to instanceof Date) {
      return Math.max(1, Math.round((date.to.getTime() - date.from.getTime()) / 86_400_000));
    }
    return 0;
  }, [date?.from, date?.to]);

  /** Departure of the current search as `YYYY-MM-DD` (local), so the
   *  strip can mark the currently-searched date. */
  const currentDepartureYMD = useMemo(() => {
    if (!(date?.from instanceof Date) || isNaN(date.from.getTime())) return null;
    const y = date.from.getFullYear();
    const m = String(date.from.getMonth() + 1).padStart(2, '0');
    const d = String(date.from.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, [date?.from]);

  /** First-of-month anchor for the strip's price window — the month of
   *  the searched departure (or today+22 default), fetched 62 days
   *  forward. Only fires when the customer opens the strip. */
  const dateStripAnchorMonth = useMemo(() => {
    const base = (date?.from instanceof Date && !isNaN(date.from.getTime()))
      ? date.from
      : (() => { const d = new Date(); d.setDate(d.getDate() + 22); return d; })();
    return new Date(Date.UTC(base.getFullYear(), base.getMonth(), 1));
  }, [date?.from]);

  /** Calendar prices feeding the CheaperAlternatives "Change dates"
   *  strip — same `/api/flight-calendar-prices` source as the picker,
   *  but gated on `dateStripOpen` so it's lazy. */
  const {
    prices: dateStripPrices,
    isLoading: dateStripLoading,
  } = useFlightCalendarPrices({
    origin,
    destination,
    startDate: dateStripAnchorMonth,
    days: 62,
    cabinClass,
    passengers,
    enabled: dateStripOpen && hasSearched,
    flightType: searchTripLengthDays > 0 ? 'round_trip' : 'one_way',
    tripLengthDays: searchTripLengthDays > 0 ? searchTripLengthDays : 7,
  });

  /** Sync `calendarViewMonth` to the month the picker will actually
   *  render when it opens. Without this the state persists at its
   *  today+22 default, so re-opening the calendar with a previously-
   *  picked date (e.g. October) fetches prices for the wrong window
   *  and no "$X" hints render on the visible dates. React-day-picker's
   *  `onMonthChange` only fires on user navigation, NOT on the initial
   *  mount / open. This effect fills that gap. */
  useEffect(() => {
    if (!isCalendarOpen) return;
    const anchor = (date?.from instanceof Date && !isNaN(date.from.getTime()))
      ? date.from
      : (() => { const d = new Date(); d.setDate(d.getDate() + 22); return d; })();
    setCalendarViewMonth(
      new Date(Date.UTC(anchor.getFullYear(), anchor.getMonth(), 1))
    );
    // date.from is a local-time Date from react-day-picker, so we read
    // local getFullYear/getMonth (not UTC variants) — otherwise a
    // near-midnight local date can land on a different UTC month and
    // the fetch window would be off by one.
  }, [isCalendarOpen, date?.from]);

  /** Track the last-searched date range so the calendar "Done"
   *  button can auto-refire the search if the customer changes
   *  dates POST-results. Without this the customer would have to
   *  manually re-click Search after every date tweak — annoying
   *  when results are already on-screen. */
  const lastSearchedDateRef = React.useRef(null);

  /** Ref to the latest `handleSearch` so `handleCalendarDone` can
   *  call it without capturing a stale closure. `handleSearch` is
   *  re-declared every render (it's not a useCallback), so a plain
   *  closure would freeze on whatever version existed the first
   *  time this component rendered. */
  const handleSearchRef = React.useRef(null);

  /** Close-the-calendar handler used by both the mobile and desktop
   *  "Done" buttons. Closes the picker; when results are already
   *  on-screen AND the dates changed since the last search, fires
   *  a fresh search on the next tick (the setTimeout lets React
   *  finish the state update from `setIsCalendarOpen` first). */
  const handleCalendarDone = () => {
    setIsCalendarOpen(false);
    if (!hasSearched) return;
    const fromMs = date?.from instanceof Date ? date.from.getTime() : null;
    const toMs = date?.to instanceof Date ? date.to.getTime() : null;
    const currentKey = `${fromMs}-${toMs}`;
    if (lastSearchedDateRef.current === currentKey) return;
    if (!fromMs) return;
    setTimeout(() => {
      if (typeof handleSearchRef.current === 'function') {
        handleSearchRef.current();
      }
    }, 0);
  };

  /** Params for the last successful outbound search — return-flight token calls must use these (state can lag). */
  const lastSuccessfulSearchParamsRef = useRef(null);
  const lastLocationSearchRef = useRef(typeof window !== 'undefined' ? window.location.search : '');

  // Load miles balance
  const loadMilesBalance = React.useCallback(async () => {
    try {
      const { UserPreferences } = await import('../api/entities');
      const balance = await UserPreferences.getMilesBalance();
      setMilesBalance(balance);
    } catch (error) {
      console.error('Failed to load miles balance:', error);
      setMilesBalance(0);
    }
  }, []);

  const loadReserveBalance = React.useCallback(async () => {
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { reserveUrl } = await import('@/lib/apiUrls');
      const { mergeReserveActAsHeaders } = await import('@/lib/reserveActAs');
      const res = await fetch(reserveUrl('balance'), {
        headers: mergeReserveActAsHeaders({ 'Authorization': `Bearer ${session.access_token}` })
      });
      if (res.ok) {
        const data = await res.json();
        setReserveBalanceCents(data.balance_cents || 0);
      }
    } catch (e) {
      setReserveBalanceCents(0);
    }
  }, []);

  React.useEffect(() => {
    loadMilesBalance();
    loadReserveBalance();
  }, [loadMilesBalance]);

  React.useEffect(() => {
    if (paymentMethod === 'miles' || paymentMethod === 'miles_installments') {
      loadMilesBalance();
    }
  }, [paymentMethod, loadMilesBalance]);

  // Flights + Stays: Defera Zero, Miles, and Reserve are not available — coerce to installments.
  React.useEffect(() => {
    if (tripMode !== 'flight_stay') return;
    if (
      paymentMethod === 'defera_zero' ||
      paymentMethod === 'miles' ||
      paymentMethod === 'miles_installments' ||
      paymentMethod === 'reserve'
    ) {
      setPaymentMethod('installments');
    }
  }, [tripMode, paymentMethod]);

  // Live search activity animation - uses shared data (200+ names × 26 initials × 250+ destinations = 1.3M+ combos)
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  
  const [searches] = useState(() => {
    const searchList = [];
    for (let i = 0; i < 1200; i++) {
      searchList.push(generateSearchActivity());
    }
    return searchList;
  });
  
  // Sporadic rotation with realistic timing - viral bursts then calm
  useEffect(() => {
    const scheduleNextRotation = () => {
      // Random interval between 1000ms and 8000ms
      // Weighted to create viral moments followed by calm periods
      const rand = Math.random();
      let delay;
      
      if (rand < 0.25) {
        // 25% chance: Viral activity (1000ms - 1500ms) - ~1 per second
        delay = 1000 + Math.random() * 500;
      } else if (rand < 0.5) {
        // 25% chance: Active period (2000ms - 3000ms) - steady pace
        delay = 2000 + Math.random() * 1000;
      } else if (rand < 0.75) {
        // 25% chance: Calm period (4000ms - 6000ms) - slowing down
        delay = 4000 + Math.random() * 2000;
      } else {
        // 25% chance: Very quiet (6000ms - 8000ms) - ~1 per 5-8 seconds
        delay = 6000 + Math.random() * 2000;
      }
      
      const timeout = setTimeout(() => {
        setCurrentSearchIndex((prev) => (prev + 1) % searches.length);
        scheduleNextRotation();
      }, delay);
      
      return timeout;
    };
    
    const timeout = scheduleNextRotation();
    return () => clearTimeout(timeout);
  }, [searches.length]);

  // Safe date formatter with validation
  const formatDate = useCallback((dateValue, formatString) => {
    try {
      if (!dateValue || !(dateValue instanceof Date) || isNaN(dateValue.getTime())) {
        return null;
      }
      return format(dateValue, formatString);
    } catch (error) {
      console.error('Error formatting date:', error);
      return null;
    }
  }, []);

  // Change Flight: trip from Trips (from Change Flight button in Flight Details) - defined early for handleDateSelect
  const changeFlightTrip = location.state?.changeFlightTrip || null;
  const isChangeFlight = !!changeFlightTrip || new URLSearchParams(location.search).get('searchMethod') === 'change_flight';

  /** "Cheaper alternatives" callout above results — SearchAPI's
   *  `google_flights` engine returns a `cheaper_alternatives` array
   *  (nearby-date pairs cheaper than the searched dates). We track
   *  the LAST SUCCESSFUL search's dates instead of the currently-
   *  picked calendar dates so tweaking the picker doesn't refire
   *  the callout — it only refreshes when new results actually
   *  arrive. Gated on `hasSearched` so we don't spend calls on
   *  empty forms.
   *
   *  Data comes in on the `cheaper_alternatives` field of the main
   *  /api/defera-flights/search response — see `handleSearch` below,
   *  which stashes it into `cheaperAlternatives` state after every
   *  successful search. No separate fetch: SearchAPI's response
   *  already rides along with the primary flight-search call, so
   *  the callout appears the moment results appear (or not at all
   *  if Google withheld the field for this route). */

  /** Apply a cheaper-alternative's date pair, close any open calendar,
   *  and refire the search. Used by the CheaperAlternatives callout.
   *  Uses `handleSearchRef` (declared further below) so the callback
   *  always invokes the freshest handleSearch closure. */
  const applyCheaperAlternativeDates = useCallback(({ from, to }) => {
    if (!(from instanceof Date) || isNaN(from.getTime())) return;
    setDate({
      from,
      to: to instanceof Date && !isNaN(to.getTime()) ? to : undefined,
    });
    setIsCalendarOpen(false);
    setTimeout(() => {
      if (typeof handleSearchRef.current === 'function') handleSearchRef.current();
    }, 0);
  }, []);

  const isChangeFlightRoundTrip = useMemo(() => {
    if (!isChangeFlight) return false;
    if (changeFlightTrip?.length) {
      const fd = changeFlightTrip[0]?.flightData || changeFlightTrip[0]?.flight_data || {};
      const slices = fd?.slices || [];
      if (slices.length >= 2) return true;
      if (changeFlightTrip.length >= 2) return true;
      return false;
    }
    return !!new URLSearchParams(location.search).get('returnDate');
  }, [isChangeFlight, changeFlightTrip, location.search]);

  // Reserve Switch: same calendar behavior as Change Flight for round trips (avoid "third click" clearing return)
  const reserveSwitchTrip = location.state?.reserveSwitchTrip || null;
  const isReserveSwitchRoundTrip = useMemo(() => {
    if (!reserveSwitchTrip) return false;
    const fd = reserveSwitchTrip.flight_data || {};
    const slices = fd?.slices || [];
    if (slices.length >= 2) return true;
    return !!new URLSearchParams(location.search).get('returnDate');
  }, [reserveSwitchTrip, location.search]);

  // Custom date handler to only allow 2 dates (start and end) - with validation.
  // `tappedDay` is the day the customer tapped (react-day-picker's second
  // onSelect argument); see src/utils/searchDateRange.js.
  const handleDateSelect = useCallback((selectedDate, tappedDay) => {
    try {
      // Change Flight keeps the trip's shape (one-way stays one-way, round
      // trip stays round trip) and reads the tapped day, so a later
      // departure can be picked (src/utils/searchDateRange.js).
      if (isChangeFlight) {
        setDate(nextChangeFlightDateRange(date, selectedDate, tappedDay, { roundTrip: isChangeFlightRoundTrip }));
        return;
      }

      // Reserve Switch keeps its dates when the calendar clears.
      if (!selectedDate && reserveSwitchTrip) return;

      // Reserve Switch (round trip): same as Change Flight — changing return must not reset the range via generic logic
      if (reserveSwitchTrip && isReserveSwitchRoundTrip) {
        const validFrom = selectedDate?.from instanceof Date && !isNaN(selectedDate.from.getTime()) ? selectedDate.from : selectedDate instanceof Date && !isNaN(selectedDate.getTime()) ? selectedDate : undefined;
        const validTo = selectedDate?.to instanceof Date && !isNaN(selectedDate.to.getTime()) ? selectedDate.to : undefined;
        setDate({ from: validFrom ?? date?.from, to: validTo ?? date?.to });
        return;
      }
      
      // With both dates picked, a day before the departure moves it and keeps
      // the return; any other day starts a new trip there.
      setDate(nextSearchDateRange(date, selectedDate, tappedDay));
    } catch (error) {
      console.error('Error handling date selection:', error);
      // Keep current state on error
    }
  }, [date, isChangeFlight, isChangeFlightRoundTrip, reserveSwitchTrip, isReserveSwitchRoundTrip]);

  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 768;
      setNumberOfMonths(mobile ? 1 : 2);
      setIsMobile(mobile);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const changeFlightAirlineIata = useMemo(() => {
    if (!changeFlightTrip?.length) return null;
    const fd = changeFlightTrip[0]?.flightData || changeFlightTrip[0]?.flight_data || {};
    const seg = fd?.slices?.[0]?.segments?.[0];
    return seg?.marketing_carrier?.iata_code || seg?.operating_carrier?.iata_code || null;
  }, [changeFlightTrip]);

  // Earliest allowed departure date for a flight change, computed live from
  // the booking's installment schedule:
  //   minDate = max(today+22, finalInstallmentDueDate + 5 days)
  // Source of truth is the schedule edge function (defera-zero / defera-capital /
  // defera-capital-nmi), NOT flight_data.payment_schedule on the booking row —
  // that field isn't reliably populated for NMI trips and can be stale.
  // We hold off rendering Search until this resolves so a customer can never
  // pick an invalid date before the floor loads.
  const [minChangeDepartureDate, setMinChangeDepartureDate] = useState(null);
  const [isLoadingChangeFloor, setIsLoadingChangeFloor] = useState(false);

  useEffect(() => {
    if (!isChangeFlight) {
      setMinChangeDepartureDate(null);
      setIsLoadingChangeFloor(false);
      return;
    }
    const trip = changeFlightTrip?.[0];
    const bookingId = trip?.id;
    if (!bookingId) {
      setMinChangeDepartureDate(null);
      return;
    }
    let aborted = false;
    setIsLoadingChangeFloor(true);
    (async () => {
      try {
        const fd = trip.flightData || trip.flight_data || {};
        const pm = fd._payment_method || fd.payment_method;
        const isNmi = pm === 'defera_capital' && fd._payment_processor === 'nmi';
        const scheduleUrl = pm === 'defera_capital'
          ? (isNmi ? deferaCapitalNmiUrl('schedule') : deferaCapitalUrl('schedule'))
          : deferaZeroUrl('schedule');
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const res = await fetch(`${scheduleUrl}?booking_id=${bookingId}`, {
          headers: mergeSupabaseEdgeHeaders({ Authorization: `Bearer ${session.access_token}` }),
        });
        const data = await res.json().catch(() => ({}));
        if (aborted || !res.ok) return;
        // Final installment = last entry in schedule.dates[].date. Schedule
        // endpoints all expose this shape (DZ / Capital Stripe / Capital NMI).
        const dates = Array.isArray(data?.schedule?.dates) ? data.schedule.dates : [];
        let finalIso = null;
        for (let i = dates.length - 1; i >= 0; i -= 1) {
          const d = dates[i]?.date;
          if (typeof d === 'string' && d.length >= 10) { finalIso = d; break; }
        }
        if (!finalIso) {
          // No future installments visible (paid-in-full, or unusual state):
          // fall back to a conservative today+22 (the existing search rule).
          setMinChangeDepartureDate(null);
          return;
        }
        // Parse YYYY-MM-DD as a LOCAL date (no TZ shift) and add 5 days.
        const [y, m, day] = finalIso.slice(0, 10).split('-').map(Number);
        const final = new Date(y, (m || 1) - 1, day || 1);
        final.setHours(0, 0, 0, 0);
        const min = new Date(final);
        min.setDate(min.getDate() + 5);
        if (!aborted) setMinChangeDepartureDate(min);
      } catch (_e) {
        if (!aborted) setMinChangeDepartureDate(null);
      } finally {
        if (!aborted) setIsLoadingChangeFloor(false);
      }
    })();
    return () => { aborted = true; };
  }, [isChangeFlight, changeFlightTrip]);

  // Once the change-flight floor lands, clear any pre-seeded departure / return
  // dates that fall below it. The picker already greys them out, but `date.from`
  // / `date.to` were auto-seeded from the original booking — leaving them set
  // would let a submit go through with a stale invalid value.
  useEffect(() => {
    if (!isChangeFlight || !minChangeDepartureDate) return;
    const fromOk = !date?.from || (date.from instanceof Date && !isNaN(date.from.getTime()) && date.from >= minChangeDepartureDate);
    const toOk = !date?.to || (date.to instanceof Date && !isNaN(date.to.getTime()) && date.to >= minChangeDepartureDate);
    if (fromOk && toOk) return;
    setDate({
      from: fromOk ? date?.from : undefined,
      to: toOk ? date?.to : undefined,
    });
  }, [isChangeFlight, minChangeDepartureDate, date?.from, date?.to]);
  const changeFlightOriginalBaseCents = useMemo(() => {
    if (!changeFlightTrip?.length) return 0;
    const main = changeFlightTrip[0];
    const fd = main?.flightData || main?.flight_data || {};
    const ps = fd?.pricing_snapshot;
    const paymentMethod = fd?._payment_method || fd?.payment_method || main?.payment_method || 'installments';
    // BASE FARE ONLY - never include service fee or hold fee
    const svc = parseFloat(ps?.service_fee || main?.service_fee || fd?.service_fee || 0);
    const hold = parseFloat(ps?.hold_fee || main?.hold_fee || fd?.hold_fee || 0);
    const gt = parseFloat(ps?.grand_total || main?.grand_total || main?.grandTotal || fd?.grand_total || 0);
    // Admin-set actual cost overrides all other base fare sources
    const actualCost = parseFloat(fd?._actual_cost ?? fd?.actual_cost ?? 0);
    if (actualCost > 0) return Math.round(actualCost * 100);
    if (ps?.original_base_fare != null && parseFloat(ps.original_base_fare) > 0) {
      let base = parseFloat(ps.original_base_fare);
      if (hold > 0 && gt > 0) {
        const derivedBase = Math.max(0, gt - svc - hold);
        if (base > derivedBase + 1) base = derivedBase;
      }
      return Math.round(base * 100);
    }
    if (paymentMethod === 'defera_zero') {
      const gtVal = parseFloat(main?.grand_total || main?.grandTotal || fd?.grand_total || 0);
      if (gtVal > 0) return Math.round(Math.max(0, gtVal - svc - hold) * 100);
    }
    const fdTotal = parseFloat(fd?.total_amount || fd?.totalAmount || 0);
    if (fdTotal > 0) return Math.round(fdTotal * 100);
    const mainTotal = parseFloat(main?.total_amount || main?.totalAmount || 0);
    const creditsTotal = parseFloat(fd?.credits_total || main?.credits_total || 0);
    const baseFromMain = creditsTotal > 0 ? mainTotal + creditsTotal : mainTotal;
    if (baseFromMain > 0) return Math.round(baseFromMain * 100);
    if (gt > 0 && svc >= 0) return Math.round(Math.max(0, gt - svc) * 100);
    return 0;
  }, [changeFlightTrip]);

  // Pre-select payment method from navigation state (e.g. Defera Zero card on Home)
  useEffect(() => {
    const statePaymentMethod = location.state?.paymentMethod;
    if (!statePaymentMethod || !['defera_zero', 'installments', 'reserve', 'miles', 'miles_installments'].includes(statePaymentMethod)) {
      return;
    }
    if (statePaymentMethod === 'defera_zero' && tripMode === 'flight_stay') return;
    setPaymentMethod(statePaymentMethod);
  }, [location.state?.paymentMethod, tripMode]);

  // When the query string changes, reset so URL prefill + auto-search can run again (e.g. another Switch Flight)
  useEffect(() => {
    if (location.search === lastLocationSearchRef.current) return;
    lastLocationSearchRef.current = location.search;
    setAutoSearchTriggered(false);
    setHasSearched(false);
  }, [location.search]);

  // Handle URL parameters from home page search (and Reserve Switch)
  useEffect(() => {
    if (autoSearchTriggered) return; // Prevent duplicate auto-search
    
    const params = new URLSearchParams(location.search);
    const urlOrigin = params.get('origin');
    const urlDestination = params.get('destination');
    const departureDate = params.get('departureDate');
    const returnDate = params.get('returnDate');
    const cabin = params.get('cabin');
    const urlTripMode = params.get('tripMode');
    const urlPaymentMethod = params.get('paymentMethod');
    const searchMethod = params.get('searchMethod');

    if (urlOrigin && urlDestination && departureDate) {
      // Pre-fill form
      setOrigin(urlOrigin);
      setDestination(urlDestination);
      
      const parsedFrom = parse(departureDate, 'yyyy-MM-dd', new Date());
      const parsedTo = returnDate ? parse(returnDate, 'yyyy-MM-dd', new Date()) : undefined;
      setDate({ from: parsedFrom, to: parsedTo });
      
      setPassengers(readPassengerParams(params));
      // Normalise on the way in. A brand label ("First") or wrong case would
      // otherwise miss both the selector (which then shows Economy) and every
      // server-side cabin table (which then searches economy).
      setCabinClass(normalizeCabinClass(cabin));
      const resolvedTripMode = urlTripMode || 'flight';
      setTripMode(resolvedTripMode);
      if (urlPaymentMethod) {
        const coerced =
          resolvedTripMode === 'flight_stay' && urlPaymentMethod === 'defera_zero'
            ? 'installments'
            : urlPaymentMethod;
        setPaymentMethod(coerced);
      }
      
      // Mark as triggered and set up search execution after state updates
      setAutoSearchTriggered(true);
    }
  }, [location.search, autoSearchTriggered]);

  // Auto-execute search when form is pre-filled from URL params
  useEffect(() => {
    if (!autoSearchTriggered) return;
    if (hasSearched) return; // Don't search again if already searched
    // In change-flight mode: don't auto-search until the installment-schedule
    // fetch has resolved. Otherwise handleSearch fires before the floor is
    // known and either (a) blocks with a confusing loading toast, or (b) lets
    // an invalid pre-seeded date slip through. The schedule effect clears
    // pre-seeded dates that fall below the floor, so by the time loading flips
    // off either `date.from` is valid or it's been wiped (the `date.from`
    // check below will then defer the search until the user picks a new one).
    if (isChangeFlight && isLoadingChangeFloor) return;

    // The URL prefill above sets every field in one batched render, so by
    // the time this runs the state is already in place: fire on the next
    // tick. (This used to wait a fixed 300ms "to give state time to
    // update", which every search launched from the home page paid.)
    if (origin && destination && date?.from) {
      const timer = setTimeout(() => {
        // Create a synthetic event and call handleSearch
        const syntheticEvent = { preventDefault: () => {} };
        handleSearch(syntheticEvent);
      }, 0);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSearchTriggered, origin, destination, date, passengers, cabinClass, hasSearched, isChangeFlight, isLoadingChangeFloor]);

  const areFlightSegmentsEquivalent = useCallback((segmentA, segmentB) => {
    if (!segmentA || !segmentB) return false;
    return (
      segmentA.origin.iata_code === segmentB.origin.iata_code &&
      segmentA.destination.iata_code === segmentB.destination.iata_code &&
      segmentA.departing_at === segmentB.departing_at &&
      segmentA.arriving_at === segmentB.arriving_at &&
      segmentA.marketing_carrier.iata_code === segmentB.marketing_carrier.iata_code &&
      segmentA.marketing_carrier_flight_number === segmentB.marketing_carrier_flight_number);
  }, []);

  const areSlicesEquivalent = useCallback((sliceA, sliceB) => {
    if (!sliceA || !sliceB || sliceA.segments.length !== sliceB.segments.length) {
      return false;
    }
    for (let i = 0; i < sliceA.segments.length; i++) {
      if (!areFlightSegmentsEquivalent(sliceA.segments[i], sliceB.segments[i])) {
        return false;
      }
    }
    return true;
  }, [areFlightSegmentsEquivalent]);

  // Index to map outbound keys to all associated offers (including return options)
  const [outboundToOffersIndex, setOutboundToOffersIndex] = useState(new Map());

  const getUniqueOutboundFlights = useCallback((offersList) => {
    console.log('getUniqueOutboundFlights processing:', offersList.length);
    
    if (!offersList || offersList.length === 0) {
      setOutboundToOffersIndex(new Map());
      return [];
    }
    
    // Group offers by unique outbound flight characteristics
    const uniqueOutboundMap = new Map();
    const newIndex = new Map(); // outboundKey -> [offers with this outbound]
    
    offersList.forEach(offer => {
      try {
        let outboundKey;
        
        if (offer.slices && offer.slices[0]) {
          // Original Duffel structure - use first slice as outbound
          const outboundSlice = offer.slices[0];
          const firstSegment = outboundSlice.segments[0];
          const lastSegment = outboundSlice.segments[outboundSlice.segments.length - 1];
          
          outboundKey = `${firstSegment.origin.iata_code}-${lastSegment.destination.iata_code}-${firstSegment.departing_at}-${firstSegment.marketing_carrier.iata_code}-${firstSegment.marketing_carrier_flight_number}`;
        } else {
          // Simplified structure - create key from main flight details
          outboundKey = `${offer.origin}-${offer.destination}-${offer.departureTime}-${offer.airline}-${offer.flightNumber}`;
        }
        
        // Build index of all offers for this outbound key
        if (!newIndex.has(outboundKey)) {
          newIndex.set(outboundKey, []);
        }
        newIndex.get(outboundKey).push(offer);
        
        // Only keep the first occurrence of each unique outbound flight for display
        if (!uniqueOutboundMap.has(outboundKey)) {
          // Attach the outbound key to the offer for later lookup
          const displayOffer = { ...offer, _outboundKey: outboundKey };
          uniqueOutboundMap.set(outboundKey, displayOffer);
        }
      } catch (error) {
        console.error('Error processing offer for deduplication:', offer, error);
        // Include the offer if we can't process it to avoid losing data
        const fallbackKey = `fallback-${offer.id || Math.random()}`;
        const displayOffer = { ...offer, _outboundKey: fallbackKey };
        uniqueOutboundMap.set(fallbackKey, displayOffer);
        newIndex.set(fallbackKey, [offer]);
      }
    });
    
    // Update the index for return flight lookup
    setOutboundToOffersIndex(newIndex);
    
    const uniqueFlights = Array.from(uniqueOutboundMap.values());
    console.log(`Deduplicated from ${offersList.length} to ${uniqueFlights.length} unique outbound flights`);
    console.log(`Built index with ${newIndex.size} outbound keys`);
    
    return uniqueFlights;
  }, []);

  /** Holds the customer's place while Duffel's extra flights (and the
   *  cheaper-dates card) land after the first results are on screen.
   *  They slot in by price — often above the flight the customer is
   *  looking at — and used to push the page out from under them. The
   *  flight they're on is noted here (an expanded one, else the one
   *  nearest the middle of the screen); the layout effect below scrolls
   *  by however far it moved, before the browser paints.
   *
   *  Only once the page is scrolled: at the very top the new flights
   *  should be seen arriving. That's the browser's own scroll-anchoring
   *  rule, which iPhone Safari doesn't implement, hence doing it here. */
  const scrollAnchorRef = useRef(null);
  const holdScrollPosition = useCallback((addedFlights = 0) => {
    if (typeof window === 'undefined' || window.scrollY < 1) return;
    const middle = window.innerHeight / 2;
    let anchor = null;
    for (const card of document.querySelectorAll('#search-results [data-offer-id]')) {
      const { top, bottom } = card.getBoundingClientRect();
      if (bottom <= 0 || top >= window.innerHeight) continue;
      const expanded = card.getAttribute('data-expanded') === 'true';
      const distance = Math.abs((top + bottom) / 2 - middle);
      if (!anchor || (expanded && !anchor.expanded) || (expanded === anchor.expanded && distance < anchor.distance)) {
        anchor = { card, top, expanded, distance };
      }
    }
    if (!anchor) return;
    scrollAnchorRef.current = {
      id: anchor.card.getAttribute('data-offer-id'),
      top: anchor.top,
      // The list takes a couple of renders to settle (allOffers → offers).
      until: performance.now() + 500,
    };
    // At most `addedFlights` can sort in above any card already shown, so
    // growing the Show-more window by that many keeps all of them — this
    // one included — on the page.
    if (addedFlights > 0) setVisibleCount((count) => count + addedFlights);
  }, []);

  useLayoutEffect(() => {
    const anchor = scrollAnchorRef.current;
    if (!anchor) return;
    if (performance.now() > anchor.until) {
      scrollAnchorRef.current = null;
      return;
    }
    const id = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(anchor.id) : anchor.id;
    const card = document.querySelector(`#search-results [data-offer-id="${id}"]`);
    if (!card) return;
    const moved = card.getBoundingClientRect().top - anchor.top;
    if (Math.abs(moved) >= 1) window.scrollBy(0, moved);
  });

  const handleSearch = async (e) => {
    // Callable both as a form submit handler (event present) and
    // programmatically (calendar "Done" auto-search below) — guard
    // the preventDefault so a bare `handleSearch()` doesn't throw.
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    // On an older build than production's, this search loads the new one:
    // the search goes in the URL and the fresh page runs it
    // (src/lib/searchNavigation.js). Not for a change-flight or a Reserve
    // switch, whose trip rides in the page's memory, which a load drops.
    if (!isChangeFlight && !reserveSwitchTrip && loadNewBuildForSearch(resultsPageUrl({
      origin, destination, from: date?.from, to: date?.to, passengers, cabinClass, tripMode, paymentMethod,
    }))) return;
    // Results stream in (Google's list first, Duffel's extras after), so
    // a search can still be running when the customer starts another one.
    // Cancel the old one and ignore anything it still delivers.
    searchRunRef.current.controller?.abort();
    const controller = new AbortController();
    const runId = searchRunRef.current.id + 1;
    searchRunRef.current = { id: runId, controller };
    const isCurrentRun = () => searchRunRef.current.id === runId;
    // Set once the first results are on screen; from then on the loading
    // flag belongs to the return-flight step, which may already be using it.
    let revealed = false;
    // How many flights are on screen, so a later batch knows how many it adds.
    let shownCount = 0;

    setHasSearched(true);
    setIsLoading(true);
    setError(null);
    setOffers([]);
    setAllOffers([]); // Clear allOffers before new search
    setVisibleCount(itemsPerPage);
    setSelectedOutboundFlight(null);
    setReturnFlightOffers([]);
    setShowReturnFlights(false);
    // Wipe stale cheaper alternatives so a lingering callout from the
    // previous search doesn't paint over an empty state while the
    // new fetch is in flight.
    setCheaperAlternatives([]);
    setSearchSpeed(null);
    // Collapse the "Change dates" strip so it doesn't stay expanded
    // (with stale prices) across a new search.
    setDateStripOpen(false);
    resetFilters();

    try {
      if (!origin || !destination) {
        throw new Error('Please select both departure and destination airports');
      }
      if (!date?.from || !(date.from instanceof Date) || isNaN(date.from.getTime())) {
        throw new Error('Please select a valid departure date');
      }

      // Save origin to localStorage for Explore page and Home page
      localStorage.setItem('lastSearchOrigin', origin);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const minBookingDate = new Date(today);
      minBookingDate.setDate(today.getDate() + 21);

      const selectedFromDate = new Date(date.from);
      selectedFromDate.setHours(0, 0, 0, 0);

      if (selectedFromDate < minBookingDate) {
        throw new Error('Departure date must be at least 21 days in advance');
      }

      // Change-flight rule: departure must be at least 5 days after final installment.
      // Only block on the loading flag when we genuinely don't know the floor yet
      // (first fetch in flight). Once a min date is known, subsequent re-searches
      // must not be blocked just because a re-fetch flipped loading on briefly.
      if (isChangeFlight && isLoadingChangeFloor && !minChangeDepartureDate) {
        throw new Error('Loading your installment schedule — please try again in a moment.');
      }
      if (isChangeFlight && minChangeDepartureDate) {
        if (selectedFromDate < minChangeDepartureDate) {
          const fmt = minChangeDepartureDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
          throw new Error(`Departure date must be at least 5 days after your final installment (${fmt} or later)`);
        }
      }

      // A round-trip change picks its departure, then its return: never
      // search it as a one-way while the return is still to be tapped.
      if (isChangeFlight && isChangeFlightRoundTrip && !(date.to instanceof Date && !isNaN(date.to.getTime()))) {
        throw new Error('Please pick your return date');
      }

      if (date.to && date.to instanceof Date && !isNaN(date.to.getTime())) {
        const selectedToDate = new Date(date.to);
        selectedToDate.setHours(0, 0, 0, 0);
        if (selectedToDate < minBookingDate) {
          throw new Error('Return date must be at least 21 days in advance');
        }
      }

      if (!passengers || passengers.length === 0) {
        throw new Error('Please select at least one passenger');
      }
      // The next empty form opens at the party searched most. A change-flight
      // or Reserve search carries a booked trip's travelers, not a choice.
      if (!isChangeFlight && !reserveSwitchTrip) rememberSearchTravelers(passengers);

      // Settings can switch Defera Flights off for an account. Reading that
      // costs a signed-in customer two database round trips, so it runs
      // alongside the search instead of in front of it: when it's off, the
      // search is cancelled and the customer redirected to Duffel search,
      // as before. A failed read no longer blocks the search — the setting
      // defaults to on.
      import('@/api/entities')
        .then(({ UserPreferences }) => UserPreferences.get())
        .then((preferences) => {
          const searchEngines = preferences?.searchEnginePreferences;
          if (searchEngines && !searchEngines.googleFlights && isCurrentRun()) {
            controller.abort();
            navigate('/flightsearch');
          }
        })
        .catch(() => {});

      const slices = [{
        origin: origin.toUpperCase(),
        destination: destination.toUpperCase(),
        departure_date: format(date.from, 'yyyy-MM-dd')
      }];

      if (date.to && date.to instanceof Date && !isNaN(date.to.getTime())) {
        slices.push({
          origin: destination.toUpperCase(),
          destination: origin.toUpperCase(),
          departure_date: format(date.to, 'yyyy-MM-dd')
        });
      }

      const params = new URLSearchParams(location.search);
      const searchMethod = params.get('searchMethod');

      const searchParams = {
        slices,
        passengers,
        cabin_class: cabinClass
      };
      if (searchMethod) searchParams.search_method = searchMethod;
      if (searchMethod === 'change_flight' && changeFlightTrip?.length && await changeSearchHidesBasic(changeFlightTrip)) {
        searchParams.exclude_basic = true;
      }

      lastSearchKeyRef.current = flightSearchKey(searchParams);

      // Ezra telemetry: what was searched, keyed to the session, so repeat
      // searches and search→checkout funnels can be mined server-side.
      const { trackEvent } = await import('@/lib/activityTracker');
      trackEvent('search_submitted', {
        origin: slices[0].origin,
        destination: slices[0].destination,
        departure_date: slices[0].departure_date,
        return_date: slices[1]?.departure_date || null,
        trip_type: slices.length > 1 ? 'round_trip' : 'one_way',
        cabin_class: cabinClass,
        travelers: passengers.length,
        engine: 'google',
        ...(searchMethod ? { search_method: searchMethod } : {}),
      });

      const startedAt = Date.now();
      let firstResultsMs = null;
      // When the server sent those first results (its own clock, from when the
      // request reached it). Far below ms_first means the time went on the way.
      let serverFirstMs = null;
      let resultsLogged = false;
      // Whether an early search (started while the form was filled in)
      // answered this one.
      let tookEarlySearch = false;
      // Priya telemetry (search quality): how many results this search
      // actually returned — zero-result searches are her core signal —
      // plus the price band, so implausible numbers get flagged before
      // customers act on them. Logged once, when the list is complete,
      // with how long the customer waited for the first results
      // (ms_first) and for all of them (ms_total).
      const logSearchResults = (flights, extra = {}) => {
        if (resultsLogged) return;
        resultsLogged = true;
        const offerPrices = flights
          .map((o) => parseFloat(o.price || o.total_amount))
          .filter((p) => Number.isFinite(p));
        trackEvent('search_results', {
          origin: slices[0].origin,
          destination: slices[0].destination,
          results: flights.length,
          engine: 'google',
          ...(offerPrices.length
            ? { price_min: Math.min(...offerPrices), price_max: Math.max(...offerPrices) }
            : {}),
          ...(firstResultsMs != null ? { ms_first: firstResultsMs } : {}),
          // (An early search's server clock started before the press, so it
          // can't be set against ms_first.)
          ...(serverFirstMs != null && !tookEarlySearch ? { server_first_ms: serverFirstMs } : {}),
          ms_total: Date.now() - startedAt,
          ...(tookEarlySearch ? { prefetched: true } : {}),
          ...extra,
        });
      };

      // Only commit params once results exist so return-flight + booking
      // options match displayed outbounds. Runs on the first results:
      // the customer can pick a flight before the rest arrive.
      const commitSearch = () => {
        lastSuccessfulSearchParamsRef.current = searchParams;
        setOriginalSearchParams(searchParams);
        // Snapshot the date range we searched with so the calendar
        // "Done" button can detect a date change and auto-fire a
        // fresh search on close (skip re-searching if the customer
        // just glanced at the calendar without touching the dates).
        lastSearchedDateRef.current = `${date?.from?.getTime?.() ?? null}-${date?.to?.getTime?.() ?? null}`;
      };

      const byPrice = (a, b) => parseFloat(a.price || a.total_amount) - parseFloat(b.price || b.total_amount);
      // Every flight is in: the speed stat's checkered flag.
      const markSpeedComplete = () => setSearchSpeed((speed) => (speed && !speed.complete ? { ...speed, complete: true } : speed));

      // Google's list first, then the full list once Duffel's extra
      // flights are merged in. The loading screen lifts on the first
      // non-empty list; an empty Google list waits for the rest, so the
      // page never says "no flights" while Duffel is still searching.
      const onFlights = (flights, { complete, serverMs }) => {
        if (!isCurrentRun()) return;
        if (flights.length === 0 && !complete) return;
        if (!revealed) {
          revealed = true;
          firstResultsMs = Date.now() - startedAt;
          serverFirstMs = Number.isFinite(serverMs) ? serverMs : null;
          setSearchSpeed({ ms: firstResultsMs, complete: Boolean(complete) });
          commitSearch();
          setIsLoading(false);
        } else {
          // Duffel's extra flights, landing on a page the customer may
          // already be reading.
          holdScrollPosition(flights.length - shownCount);
        }
        shownCount = flights.length;
        setAllOffers([...flights].sort(byPrice));
        if (complete) {
          logSearchResults(flights);
          markSpeedComplete();
        }
      };

      // If this exact search already started while the form was being
      // filled in (here or on the home page), take it over: whatever has
      // arrived shows now and the rest streams in. Otherwise — no early
      // search, or one for something else, which is dropped — search now.
      const earlySearch = takePrefetchedFlightSearch(searchParams);
      let searchDone;
      if (earlySearch) {
        tookEarlySearch = true;
        controller.signal.addEventListener('abort', earlySearch.abort, { once: true });
        earlySearch.attach(onFlights);
        searchDone = earlySearch.result;
      } else {
        searchDone = streamGoogleFlights(searchParams, { signal: controller.signal, onFlights });
      }
      const { data: responseData, status, meta } = await searchDone;
      if (!isCurrentRun()) return;

      // `meta.cheaperAlternatives` is SearchAPI's `cheaper_alternatives`
      // list, folded into the primary search response. Stash it in
      // component state so the CheaperAlternatives callout can render
      // — see `cheaperAlternatives` below. Google withholds this field
      // on most routes; empty array is normal. The card sits above the
      // "Other flights" list, so when it arrives after the flights are
      // on screen, hold the customer's place for it too.
      const cheaperAlternativesFound = Array.isArray(meta?.cheaperAlternatives) ? meta.cheaperAlternatives : [];
      if (revealed && cheaperAlternativesFound.length > 0) holdScrollPosition();
      setCheaperAlternatives(cheaperAlternativesFound);

      if (status !== 200) {
        // Check if it's a 404 (no flights found)
        if (status === 404) {
          // Priya telemetry: a zero-result search is her core quality
          // signal. The API no longer 404s — "no flights" arrives as an
          // empty list and is logged as results: 0 below — but this stays
          // for any server that still does.
          trackEvent('search_results', {
            origin: slices[0].origin,
            destination: slices[0].destination,
            results: 0,
            engine: 'google',
          });
          const noFlightsMessage = responseData?.message || responseData?.error || 'No flights available for the specified criteria. Try different dates or airports.';
          throw new Error(noFlightsMessage);
        }
        const errorMessage = responseData?.error || `Request failed with status: ${status}`;
        throw new Error(errorMessage);
      }

      console.log('Backend response data:', responseData);

      commitSearch();
      // Already logged if the list completed while streaming; this covers
      // a one-body reply and a stream that broke before Duffel's extras.
      logSearchResults(Array.isArray(responseData) ? responseData : [], meta?.partial ? { partial: true } : {});

      if (Array.isArray(responseData) && responseData.length > 0) {
        const offers = responseData;
        console.log(`Received ${offers.length} offers from backend`);

        // Use the offers directly from the flights array
        setAllOffers([...offers].sort(byPrice));
      } else {
        console.log('No offers found in response or invalid format');
        console.log('ResponseData:', responseData);
        setOffers([]);
        setAllOffers([]);
      }
    } catch (err) {
      // A newer search, a Settings redirect or leaving the page cancelled this one.
      if (!isCurrentRun() || err?.name === 'AbortError') return;
      console.error('Search failed:', err);
      setError(err.message || 'An unexpected error occurred during the search.');
    } finally {
      if (isCurrentRun() && !revealed) setIsLoading(false);
      // The stream is over, however it ended: no more flights are coming.
      if (isCurrentRun() && revealed) setSearchSpeed((speed) => (speed && !speed.complete ? { ...speed, complete: true } : speed));
    }
  };

  // Keep `handleSearchRef` in sync with the latest `handleSearch`
  // closure on every render. `handleCalendarDone` reads through the
  // ref so a Done click always fires the freshest search logic —
  // otherwise the callback would capture whatever `handleSearch`
  // was on the render the button was first mounted.
  useEffect(() => {
    handleSearchRef.current = handleSearch;
  });

  // Leaving the page mid-search stops reading its results stream.
  useEffect(() => () => searchRunRef.current.controller?.abort(), []);

  // Start the search before Search is pressed: once the form holds a
  // complete search that isn't the one on screen, run it quietly so the
  // press can show it at once (src/lib/searchPrefetch.js). Not while the
  // date picker or travelers picker is open (the search is still being
  // chosen; closing either picker starts it at once, see useSearchPickers)
  // and not while a search is running. A change-flight search keeps the rules the press
  // applies: no departure before the installment floor, and Basic Economy
  // hidden once the ticket is booked. Editing the search also re-checks for
  // a newer Defera, which Search would then load (src/lib/searchNavigation.js).
  useEffect(() => {
    checkBuildWhileSearching();
    if (isCalendarOpen || isTravelersOpen || isLoading) return undefined;
    const params = buildFlightSearchParams({ origin, destination, from: date?.from, to: date?.to, passengers, cabinClass });
    if (!params || flightSearchKey(params) === lastSearchKeyRef.current) return undefined;
    if (isChangeFlight) {
      if (isChangeFlightRoundTrip && !date?.to) return undefined;
      if (isLoadingChangeFloor && !minChangeDepartureDate) return undefined;
      const departs = new Date(date.from);
      departs.setHours(0, 0, 0, 0);
      if (minChangeDepartureDate && departs < minChangeDepartureDate) return undefined;
    }
    const changeRules = new URLSearchParams(location.search).get('searchMethod') === 'change_flight' && changeFlightTrip?.length > 0;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        if (changeRules && await changeSearchHidesBasic(changeFlightTrip)) params.exclude_basic = true;
      } catch {
        return; // The press searches (and reports a failure) as usual.
      }
      // Search may have been pressed for this search in the meantime.
      if (!cancelled && flightSearchKey(params) !== lastSearchKeyRef.current) prefetchFlightSearch(params);
    }, earlySearchDelay());
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [origin, destination, date?.from, date?.to, passengers, cabinClass, isCalendarOpen, isTravelersOpen, earlySearchDelay, isLoading, isChangeFlight, isChangeFlightRoundTrip, isLoadingChangeFloor, minChangeDepartureDate, changeFlightTrip, location.search]);

  // Helper to clean airline name (Defera Flights style - just the name, no suffix)
  const cleanAirlineName = useCallback((name) => {
    if (!name) return '';
    return name
      .replace(/\s+(Airlines|Airways|Air Lines|Airline)\s*$/i, '')
      .trim();
  }, []);

  const getOfferAirlineIata = useCallback((offer) => {
    const seg = offer?.slices?.[0]?.segments?.[0];
    return seg?.marketing_carrier?.iata_code || seg?.operating_carrier?.iata_code || null;
  }, []);

  const getAirlineName = useCallback((offer) => {
    // Handle both transformed and original Duffel structures
    let name = '';
    if (offer.owner?.name) name = offer.owner.name;
    else if (offer.airline) name = offer.airline;
    else if (offer.slices?.[0]?.segments?.[0]?.marketing_carrier?.name) {
      name = offer.slices[0].segments[0].marketing_carrier.name;
    } else {
      name = 'Unknown Airline';
    }
    return cleanAirlineName(name);
  }, [cleanAirlineName]);

  // Memoize the initial set of unique outbound flights from all raw offers
  const rawUniqueOutboundFlights = useMemo(() => {
    console.log('Processing allOffers in rawUniqueOutboundFlights:', allOffers.length);
    const uniqueFlights = getUniqueOutboundFlights(allOffers);
    console.log('Unique outbound flights found:', uniqueFlights.length);
    return uniqueFlights;
  }, [allOffers, getUniqueOutboundFlights]);

  // Memoize the unique airlines based on the initial set of unique outbound flights
  const uniqueAirlines = useMemo(() => {
    const airlines = rawUniqueOutboundFlights.map(getAirlineName);
    return [...new Set(airlines)];
  }, [rawUniqueOutboundFlights, getAirlineName]);

  const processedFlights = useMemo(() => {
    console.log('Processing flights with rawUniqueOutboundFlights:', rawUniqueOutboundFlights.length);
    let flights = [...rawUniqueOutboundFlights]; // Use the memoized raw unique flights as base
    console.log('Initial flights count:', flights.length);

    // Change Flight: only same airline as original booking (funds tied to airline)
    if (changeFlightAirlineIata) {
      flights = flights.filter((offer) => {
        const offerIata = getOfferAirlineIata(offer);
        return offerIata && offerIata.toUpperCase() === changeFlightAirlineIata.toUpperCase();
      });
      console.log('After Change Flight airline filter:', flights.length);
    }

    // Defera Zero: only eligible airlines (United, American, Southwest, JetBlue, Virgin Atlantic)
    if (paymentMethod === 'defera_zero') {
      flights = flights.filter((offer) => {
        const airline = getAirlineName(offer).toLowerCase();
        return DEFERA_ZERO_ALLOWED_AIRLINES.some((allowed) => airline.includes(allowed));
      });
      console.log('After Defera Zero airline filter:', flights.length);
    }

    if (airlineFilter !== 'all') {
      flights = flights.filter((offer) => getAirlineName(offer) === airlineFilter);
      console.log('After airline filter:', flights.length);
    }

    if (stopsFilter !== 'any') {
      flights = flights.filter((offer) => {
        try {
          // Handle both old and new data structures
          if (offer.slices && offer.slices[0] && offer.slices[0].segments) {
            const stops = offer.slices[0].segments.length - 1;
            if (stopsFilter === '0') return stops === 0;
            if (stopsFilter === '1') return stops === 1;
          } else if (offer.stops !== undefined) {
            // Use simplified structure
            if (stopsFilter === '0') return offer.stops === 0;
            if (stopsFilter === '1') return offer.stops === 1;
          }
          return true;
        } catch (error) {
          console.error('Error filtering stops for offer:', offer, error);
          return true;
        }
      });
      console.log('After stops filter:', flights.length);
    }

    flights.sort((a, b) => {
      try {
        // 'best' sorts the flat list by price too — the Best block is
        // pinned separately from google_best_rank, and the Other list
        // reads cheapest-first beneath it.
        if (sortBy === 'price' || sortBy === 'best') {
          const priceA = parseFloat(a.price || a.total_amount || 0);
          const priceB = parseFloat(b.price || b.total_amount || 0);
          return priceA - priceB;
        }
        if (sortBy === 'duration') {
          return getOutboundDurationMinutes(a) - getOutboundDurationMinutes(b);
        }
        if (sortBy === 'departure') {
          return getOutboundDepartureMs(a) - getOutboundDepartureMs(b);
        }
        return 0;
      } catch (error) {
        console.error('Error sorting flights:', error);
        return 0;
      }
    });

    console.log('Final processed flights count:', flights.length);
    return flights;
  }, [rawUniqueOutboundFlights, airlineFilter, stopsFilter, sortBy, getAirlineName, getOfferAirlineIata, paymentMethod, changeFlightAirlineIata]);

  useEffect(() => {
    setVisibleCount(itemsPerPage);
  }, [airlineFilter, stopsFilter, sortBy]);

  // Google's "Best flights" block — their ranked picks (a value blend of
  // price, duration, and stops), pinned above the full list exactly like
  // Google Flights does. Only under the default 'best' sort: an explicit
  // price/duration/departure sort dissolves the sections into one flat
  // sorted list, which is also what Google itself does. Filters still
  // apply — a filtered-out best flight drops from the block too.
  const bestOutboundFlights = useMemo(() => {
    if (sortBy !== 'best') return [];
    return processedFlights
      .filter((f) => f.google_best)
      .sort((a, b) => (a.google_best_rank ?? 99) - (b.google_best_rank ?? 99));
  }, [processedFlights, sortBy]);

  const otherOutboundFlights = useMemo(() => {
    if (bestOutboundFlights.length === 0) return processedFlights;
    return processedFlights.filter((f) => !f.google_best);
  }, [processedFlights, bestOutboundFlights]);

  // Memoized so the effect below can depend on the slice itself (a bare
  // .slice() re-creates the array every render, which would loop the
  // effect). Depending on the list length instead — the old approach —
  // missed re-SORTS entirely: reordering keeps the length constant, so
  // `offers` silently kept the stale order and the Sort dropdown
  // appeared to do nothing. The Show-more window covers the "Other
  // flights" list only; the Best block is always fully visible.
  const visibleOutboundOffers = useMemo(
    () => otherOutboundFlights.slice(0, visibleCount),
    [otherOutboundFlights, visibleCount]
  );
  const hiddenOutboundCount = Math.max(0, otherOutboundFlights.length - visibleCount);

  useEffect(() => {
    if (!selectedOutboundFlight && !showReturnFlights) {
      setOffers(visibleOutboundOffers);
    }
  }, [visibleOutboundOffers, selectedOutboundFlight, showReturnFlights]);

  const handleShowMore = () => {
    setVisibleCount((count) => count + itemsPerPage);
  };

  // Return-leg mirror of the outbound Best-flights presentation: Google's
  // ranked picks for THIS return leg pinned first (tagged by the same
  // transformSerpAPIToDuffel pass on the departure_token fetch), everything
  // else cheapest-first behind Show more — the Google Flights layout.
  // Returns have no sort dropdown, so 'best' is effectively always on.
  const bestReturnFlights = useMemo(
    () => returnFlightOffers
      .filter((f) => f.google_best)
      .sort((a, b) => (a.google_best_rank ?? 99) - (b.google_best_rank ?? 99)),
    [returnFlightOffers]
  );
  const otherReturnFlights = useMemo(() => {
    const rest = bestReturnFlights.length === 0
      ? returnFlightOffers
      : returnFlightOffers.filter((f) => !f.google_best);
    return [...rest].sort(
      (a, b) => parseFloat(a.price || a.total_amount || 0) - parseFloat(b.price || b.total_amount || 0)
    );
  }, [returnFlightOffers, bestReturnFlights]);
  const visibleReturnFlights = useMemo(
    () => otherReturnFlights.slice(0, returnVisibleCount),
    [otherReturnFlights, returnVisibleCount]
  );
  const hiddenReturnCount = Math.max(0, otherReturnFlights.length - returnVisibleCount);

  const handleFlightSelection = async (selectedOffer) => {
    console.log('handleFlightSelection called with:', selectedOffer);
    console.log('Has booking_token?', !!selectedOffer.booking_token);
    console.log('booking_token value:', selectedOffer.booking_token);
    
    if (!date.to) {
      // One-way trip
      if (tripMode === 'flight_stay') {
        // For Flights + Stays, show error - round trip required
        toast.error('Flights + Stays requires a round trip. Please select return dates.');
        return;
      }
      
      // Regular one-way flight - navigate to booking options
      if (selectedOffer.booking_token) {
        console.log('Navigating to booking options with token:', selectedOffer.booking_token);
        const navState = {
          bookingToken: selectedOffer.booking_token,
          searchParams: lastSuccessfulSearchParamsRef.current || originalSearchParams,
          selectedFlight: selectedOffer,
          returnFlight: null, // Explicitly clear - prevents stale return from previous round-trip
          paymentMethod: paymentMethod,
          milesBalance: milesBalance,
          reserveBalanceCents: reserveBalanceCents,
          deferaZero: paymentMethod === 'defera_zero' && tripMode !== 'flight_stay',
          packageType: tripMode === 'flight_stay' ? 'flight_stay' : 'flight',
        };
        if (reserveSwitchTrip) navState.reserveSwitchTrip = reserveSwitchTrip;
        if (changeFlightTrip) navState.changeFlightTrip = changeFlightTrip;
        navigate(createPageUrl('FlightOptions'), { state: navState });
      } else {
        // Every source gives a bookable flight a booking token (Duffel and
        // Mondee mint their own), and the price is the fare picked on fare
        // options. This flight has none: it can't be priced honestly, so it
        // isn't sold. (It used to go straight to Review priced from the card,
        // for whichever travelers were in the form at the time.)
        console.warn('Offer has no booking_token — not bookable', { offerId: selectedOffer?.id });
        toast.error(UNBOOKABLE_FLIGHT_MESSAGE);
      }
      return;
    }

    // Round-trip - use departure_token to fetch return flights.
    //
    // This works uniformly for SerpAPI and Duffel outbounds: the backend
    // projects round-trip Duffel offers down to outbound-only 1-slice
    // offers with a synthetic `duffel:v1:...` departure_token, and
    // `/api/defera-flights/search-with-token` routes those tokens to a
    // fresh Duffel search filtered by the encoded outbound signature.
    // The client stays source-agnostic.
    console.log('Outbound flight selected:', selectedOffer);
    setSelectedOutboundFlight(selectedOffer);

    // FAST PATH — Duffel round-trip outbounds carry the matching return
    // legs inline as `_duffel_return_options` on the initial search
    // response. Populate the "Select a return flight" list from local
    // state instead of round-tripping /api/defera-flights/search-with-
    // token (which re-runs a fresh Duffel offer request — ~1-3s of
    // waiting). The token path stays as a fallback for stale clients
    // and edge cases where the inline data is missing.
    const inlineDuffelReturns = Array.isArray(selectedOffer?._duffel_return_options)
      ? selectedOffer._duffel_return_options
      : null;
    if (selectedOffer.duffel_source && inlineDuffelReturns && inlineDuffelReturns.length > 0) {
      let offersToShow = inlineDuffelReturns
        .slice()
        .sort((a, b) => parseFloat(a.total_amount || 0) - parseFloat(b.total_amount || 0));
      if (changeFlightAirlineIata && offersToShow.length > 0) {
        offersToShow = offersToShow.filter((offer) => {
          const offerIata = getOfferAirlineIata(offer);
          return offerIata && offerIata.toUpperCase() === changeFlightAirlineIata.toUpperCase();
        });
      }
      if (paymentMethod === 'defera_zero' && offersToShow.length > 0) {
        offersToShow = offersToShow.filter((offer) => {
          const airline = getAirlineName(offer).toLowerCase();
          return DEFERA_ZERO_ALLOWED_AIRLINES.some((a) => airline.includes(a));
        });
      }
      setReturnFlightOffers(offersToShow);
      setShowReturnFlights(true);
      if (offersToShow.length > 0) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }

    if (!selectedOffer.departure_token) {
      // A round-trip outbound with no way to fetch its returns can't be
      // priced as a round trip. It used to go to Review as a one-way.
      console.warn('Round-trip outbound missing departure_token — not bookable', {
        offerId: selectedOffer?.id,
        duffelSource: selectedOffer?.duffel_source === true,
      });
      setSelectedOutboundFlight(null);
      toast.error(UNBOOKABLE_FLIGHT_MESSAGE);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const effectiveSearchParams = lastSuccessfulSearchParamsRef.current || originalSearchParams;
      if (!effectiveSearchParams?.slices?.length) {
        toast.error('Search parameters missing — run a new search and pick an outbound flight again.');
        return;
      }
      // Fetch return flights using departure_token and the same slices as the outbound search (ref avoids stale state)
      const returnFlightsData = await searchGoogleFlightsWithToken(selectedOffer.departure_token, effectiveSearchParams, { parentSearchId: selectedOffer._search_id });

      let offersToShow = returnFlightsData || [];
      if (changeFlightAirlineIata && offersToShow.length > 0) {
        offersToShow = offersToShow.filter((offer) => {
          const offerIata = getOfferAirlineIata(offer);
          return offerIata && offerIata.toUpperCase() === changeFlightAirlineIata.toUpperCase();
        });
      }
      if (paymentMethod === 'defera_zero' && offersToShow.length > 0) {
        offersToShow = offersToShow.filter((offer) => {
          const airline = getAirlineName(offer).toLowerCase();
          return DEFERA_ZERO_ALLOWED_AIRLINES.some((a) => airline.includes(a));
        });
      }

      setReturnVisibleCount(itemsPerPage);
      if (offersToShow.length > 0) {
        setReturnFlightOffers(offersToShow);
        setShowReturnFlights(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setReturnFlightOffers([]);
        setShowReturnFlights(true);
      }
    } catch (err) {
      console.error('Failed to fetch return flights:', err);
      toast.error('Failed to load return flights');
      setReturnFlightOffers([]);
      setShowReturnFlights(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeselectOutbound = () => {
    setSelectedOutboundFlight(null);
    setShowReturnFlights(false);
    setReturnFlightOffers([]);
    // visibleCount is deliberately NOT reset here: the deselected flight
    // may sit past the first 30, and collapsing the list would hide it.
  };

  const handleReturnFlightSelection = (returnOffer) => {
    console.log('handleReturnFlightSelection called with:', returnOffer);
    console.log('Return flight has booking_token?', !!returnOffer.booking_token);
    console.log('Return flight booking_token value:', returnOffer.booking_token);
    
    // Navigate to booking options with booking_token
    if (returnOffer.booking_token) {
      console.log('Navigating to booking options with return flight token:', returnOffer.booking_token);
      
      const navState = {
        bookingToken: returnOffer.booking_token,
        searchParams: lastSuccessfulSearchParamsRef.current || originalSearchParams,
        selectedFlight: selectedOutboundFlight,
        returnFlight: returnOffer,
        tripMode: tripMode,
        packageType: tripMode === 'flight_stay' ? 'flight_stay' : 'flight',
        paymentMethod: paymentMethod,
        milesBalance: milesBalance,
        reserveBalanceCents: reserveBalanceCents,
        deferaZero: paymentMethod === 'defera_zero' && tripMode !== 'flight_stay',
      };
      if (reserveSwitchTrip) navState.reserveSwitchTrip = reserveSwitchTrip;
      if (changeFlightTrip) navState.changeFlightTrip = changeFlightTrip;
      navigate(createPageUrl('FlightOptions'), { state: navState });
    } else {
      // No booking token: no fare options, so no honest price for the pair.
      // (The old fallback priced it from the cards, and Review then added
      // the return card to an outbound that already held the round trip.)
      console.warn('Return offer has no booking_token — not bookable', { offerId: returnOffer?.id });
      toast.error(UNBOOKABLE_FLIGHT_MESSAGE);
    }
  };


  const resetFilters = () => {
    setSortBy('best');
    setStopsFilter('any');
    setAirlineFilter('all');
  };

  // The "cheaper dates" price-comparison card, hoisted so it can render
  // in either of two slots without duplicating the prop block: between
  // the Best flights block and Other flights when the sections show, or
  // at the top of the results as the fallback. Only while the customer
  // is still picking an OUTBOUND flight (hidden once they've selected
  // one or are looking at return legs, and hidden for change-flight
  // which doesn't shop dates). SearchAPI decides which alternatives are
  // cheaper than the current search; with none, this is null.
  const cheaperAlternativesCard = !selectedOutboundFlight && !showReturnFlights && cheaperAlternatives.length > 0 ? (
    <CheaperAlternatives
      alternatives={cheaperAlternatives}
      onSelectDates={applyCheaperAlternativeDates}
      datePrices={dateStripPrices}
      datePricesLoading={dateStripLoading}
      tripLengthDays={searchTripLengthDays}
      currentDeparture={currentDepartureYMD}
      open={dateStripOpen}
      onToggleDates={setDateStripOpen}
    />
  ) : null;

  // The speed stat over the results (SearchSpeedStat): centered above the
  // filters on phones, in the "Select an outbound flight" row on desktop
  // (founder, 10-22).
  const showSpeedStat = Boolean(searchSpeed) && !error && !selectedOutboundFlight && !showReturnFlights && rawUniqueOutboundFlights.length > 0;
  const pillGlassClass = 'backdrop-blur-md border-[0.5px] border-white/20 text-white/90 hover:text-white bg-[linear-gradient(0deg,rgba(255,255,255,0.075)_0%,rgba(255,255,255,0.012)_35%,rgba(255,255,255,0)_100%)] hover:bg-[linear-gradient(0deg,rgba(255,255,255,0.1)_0%,rgba(255,255,255,0.02)_35%,rgba(255,255,255,0)_100%)] hover:border-white/28 active:bg-[linear-gradient(0deg,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.005)_35%,transparent_100%)] shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.58),inset_0_-0.5px_0_0_rgba(255,255,255,0.2),inset_0_0_0_0.5px_rgba(255,255,255,0.1),0_4px_16px_rgba(0,0,0,0.22)] hover:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.52),inset_0_-0.5px_0_0_rgba(255,255,255,0.26),inset_0_0_0_0.5px_rgba(255,255,255,0.14),0_6px_20px_rgba(0,0,0,0.26)] active:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.62),inset_0_-0.5px_0_0_rgba(255,255,255,0.12),inset_0_0_0_0.5px_rgba(255,255,255,0.06),0_2px_12px_rgba(0,0,0,0.2)]';

  return (
    <>
      <MarketingTags contentsquare />
      <div
        className="min-h-screen w-full transition-colors duration-500 relative"
        style={{
          background: hasSearched ? 'transparent' : 'linear-gradient(to bottom, #0a0a0a 0%, #05091E 15%, #091034 35%, #0F1952 55%, #080E28 75%, #4E6199 90%, #D5DAEB 100%)',
        }}
      >
      {/* Royal blue ambient wash — same treatment as the Rewards page,
          shown once search results are on screen */}
      {hasSearched && <RoyalAmbientBackground />}
      <div className={`w-full transition-all duration-500 ease-in-out relative z-10 ${!hasSearched ? 'min-h-[70vh] flex items-center justify-center' : ''}`}>
      
      {/* The loading screens fade in but leave the instant flights arrive:
          outside AnimatePresence they unmount in the same render as the
          results, with no exit animation. */}
      <>
        {/* INITIAL SEARCH TUNNEL - Hexagons variant */}
        {isLoading && !selectedOutboundFlight &&
        <motion.div
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}>

            {/* Full-screen immersive tunnel - lighter set for performance */}
            <div className="absolute inset-0 overflow-hidden perspective-container">
              <div className="tunnel-hexagons">
                {[...Array(2)].map((_, i) => (
                  <div key={i} className="giant-hexagon" style={{ animationDelay: `${i * 0.5}s` }} />
                ))}
              </div>
              <div className="immersive-circles">
                {[...Array(2)].map((_, i) => (
                  <div key={i} className="mega-circle" style={{ animationDelay: `${i * 0.5}s` }} />
                ))}
              </div>
              <div className="glitter-field">
                {[8, 25, 42, 58, 75, 92].map((left, i) => (
                  <div key={i} className="glitter-star" style={{ left: `${left}%`, top: `${20 + i * 12}%`, animationDelay: `${i * 0.3}s` }} />
                ))}
              </div>
            </div>

            <motion.div className="relative z-10 text-center" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}>
              <div className="w-24 h-24 mx-auto mb-8 relative flex items-center justify-center">
                <div className="tunnel-glow" />
                <Plane className="w-14 h-14 relative z-10 text-white drop-shadow-[0_0_24px_rgba(255,255,255,0.45)]" />
              </div>
              
              <h3 className="text-2xl font-light text-white mb-3 tracking-tight animate-pulse-text">Searching the Skies</h3>
              <p className="text-white/60 max-w-sm mx-auto text-sm font-normal leading-relaxed">
                Finding the perfect journey for you
              </p>
            </motion.div>

            <style>{`
              .perspective-container { perspective: 800px; perspective-origin: 50% 50%; }
              .tunnel-hexagons { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: none; }
              .giant-hexagon {
                position: absolute;
                width: min(480px, 85vmax);
                height: min(480px, 85vmax);
                background: linear-gradient(60deg, transparent 30%, rgba(16, 37, 178, 0.5) 45%, rgba(16, 37, 178, 0.8) 50%, rgba(16, 37, 178, 0.5) 55%, transparent 70%);
                clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
                box-shadow: 0 0 80px rgba(16, 37, 178, 0.5);
                animation: tunnel-fly 3s ease-in-out infinite;
              }
              @keyframes tunnel-fly {
                0% { transform: translateZ(-800px) scale(0.3) rotate(0deg); opacity: 0; }
                25% { opacity: 1; }
                50% { transform: translateZ(0) scale(1.4) rotate(180deg); opacity: 1; }
                75% { opacity: 0.8; }
                100% { transform: translateZ(600px) scale(2.8) rotate(360deg); opacity: 0; }
              }
              .immersive-circles { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: none; }
              .mega-circle {
                position: absolute;
                width: min(280px, 50vmax);
                height: min(280px, 50vmax);
                border-radius: 50%;
                border: 3px solid rgba(16, 37, 178, 0.6);
                animation: mega-expand 3.2s ease-out infinite;
              }
              @keyframes mega-expand {
                0% { transform: scale(0.3); opacity: 0; }
                30% { opacity: 1; }
                60% { transform: scale(2.5); opacity: 0.7; }
                100% { transform: scale(4.5); opacity: 0; }
              }
              .glitter-field { position: absolute; inset: 0; pointer-events: none; }
              .glitter-star {
                position: absolute;
                width: 5px;
                height: 5px;
                background: rgba(16, 37, 178, 0.9);
                border-radius: 50%;
                animation: glitter-twinkle 2s ease-in-out infinite;
              }
              @keyframes glitter-twinkle {
                0%, 100% { opacity: 0.2; transform: scale(0.8); }
                50% { opacity: 1; transform: scale(1.2); }
              }
              .tunnel-glow {
                position: absolute;
                inset: -80px;
                background: radial-gradient(circle, rgba(16, 37, 178, 0.6) 0%, rgba(16, 37, 178, 0.28) 50%, transparent 70%);
                border-radius: 50%;
                animation: tunnel-pulse 2.5s ease-in-out infinite;
              }
              @keyframes tunnel-pulse {
                0%, 100% { transform: scale(0.9); opacity: 0.6; }
                50% { transform: scale(1.6); opacity: 1; }
              }
              .animate-pulse-text { animation: pulse-text 1.5s ease-in-out infinite; }
              @keyframes pulse-text { 0%, 100% { opacity: 1; } 50% { opacity: 0.7; } }
            `}</style>
          </motion.div>
        }

        {isLoading && selectedOutboundFlight &&
        <motion.div
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}>

            <div className="absolute inset-0 overflow-hidden perspective-container">
              <div className="tunnel-diamonds">
                {[...Array(2)].map((_, i) => (
                  <div key={i} className="giant-diamond" style={{ animationDelay: `${i * 0.5}s` }} />
                ))}
              </div>
              <div className="immersive-triangles">
                {[...Array(2)].map((_, i) => (
                  <div key={i} className="mega-triangle" style={{ transform: `rotate(${i * 180}deg)`, animationDelay: `${i * 0.4}s` }} />
                ))}
              </div>
              <div className="glitter-field">
                {[12, 35, 60, 85].map((left, i) => (
                  <div key={i} className="glitter-star" style={{ left: `${left}%`, top: `${25 + i * 18}%`, animationDelay: `${i * 0.35}s` }} />
                ))}
              </div>
            </div>

            <motion.div className="relative z-10 text-center" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}>
              <div className="w-24 h-24 mx-auto mb-8 relative flex items-center justify-center">
                <div className="tunnel-glow" />
                <Plane className="w-14 h-14 relative z-10 text-white drop-shadow-[0_0_24px_rgba(255,255,255,0.45)]" />
              </div>
              
              <h3 className="text-2xl font-light text-white mb-3 tracking-tight animate-pulse-text">Journey Home</h3>
              <p className="text-white/60 max-w-sm mx-auto text-sm font-normal leading-relaxed">
                Preparing your return
              </p>
            </motion.div>

            <style>{`
              .perspective-container { perspective: 800px; perspective-origin: 50% 50%; }
              .tunnel-diamonds { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: none; }
              .giant-diamond {
                position: absolute;
                width: min(500px, 90vmax);
                height: min(500px, 90vmax);
                background: linear-gradient(135deg, transparent 30%, rgba(16, 37, 178, 0.5) 45%, rgba(16, 37, 178, 0.8) 50%, rgba(16, 37, 178, 0.5) 55%, transparent 70%);
                transform: rotate(45deg);
                box-shadow: 0 0 80px rgba(16, 37, 178, 0.5);
                animation: tunnel-fly-return 3s ease-in-out infinite;
              }
              @keyframes tunnel-fly-return {
                0% { transform: rotate(45deg) translateZ(-800px) scale(0.3); opacity: 0; }
                25% { opacity: 1; }
                50% { transform: rotate(45deg) translateZ(0) scale(1.4); opacity: 1; }
                75% { opacity: 0.8; }
                100% { transform: rotate(45deg) translateZ(600px) scale(2.8); opacity: 0; }
              }
              .immersive-triangles { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: none; }
              .mega-triangle {
                position: absolute;
                width: 0;
                height: 0;
                border-left: min(280px, 50vmax) solid transparent;
                border-right: min(280px, 50vmax) solid transparent;
                border-bottom: min(480px, 85vmax) solid rgba(16, 37, 178, 0.5);
                animation: mega-spin 3.2s ease-in-out infinite;
                transform-origin: 50% 66%;
              }
              @keyframes mega-spin {
                0%, 100% { transform: scale(0.4) rotate(0deg); opacity: 0.3; }
                50% { transform: scale(2) rotate(180deg); opacity: 0.65; }
              }
              .glitter-field { position: absolute; inset: 0; pointer-events: none; }
              .glitter-star {
                position: absolute;
                width: 5px;
                height: 5px;
                background: rgba(16, 37, 178, 0.9);
                border-radius: 50%;
                animation: glitter-twinkle 2s ease-in-out infinite;
              }
              @keyframes glitter-twinkle {
                0%, 100% { opacity: 0.2; transform: scale(0.8); }
                50% { opacity: 1; transform: scale(1.2); }
              }
              .tunnel-glow {
                position: absolute;
                inset: -80px;
                background: radial-gradient(circle, rgba(16, 37, 178, 0.6) 0%, rgba(16, 37, 178, 0.28) 50%, transparent 70%);
                border-radius: 50%;
                animation: tunnel-pulse 2.5s ease-in-out infinite;
              }
              @keyframes tunnel-pulse {
                0%, 100% { transform: scale(0.9); opacity: 0.6; }
                50% { transform: scale(1.6); opacity: 1; }
              }
              .animate-pulse-text { animation: pulse-text 1.5s ease-in-out infinite; }
              @keyframes pulse-text { 0%, 100% { opacity: 1; } 50% { opacity: 0.7; } }
            `}</style>
          </motion.div>
        }

      </>
      
      <div className={`w-full px-6 md:px-8 max-w-4xl mx-auto ${hasSearched ? 'py-8' : ''}`}>
        <div className="w-full">
          {!hasSearched && (
            <motion.div layout>
              <div className="text-center mb-4 md:mb-6">
                <h2 className="text-3xl md:text-5xl lg:text-6xl font-light text-white mb-3 tracking-tight flex items-center justify-center gap-3">Search flights</h2>
                <p className="text-white/60 text-sm md:text-base lg:text-lg font-light mx-auto max-w-md">
                  Let's find your next destination.
                </p>
              </div>
            </motion.div>
          )}

          {/* The layout animation is for the form gliding from the middle
              of the landing view to the top on a search, and it stops once
              results are on screen. Results arrive in two batches now
              (Google's, then Duffel's extra flights), and a live `layout`
              turned the second batch into an animation of the whole
              results list — the page visibly squashed and slid under the
              customer, and it undid holdScrollPosition's correction. */}
          <motion.div
            layout={allOffers.length > 0 ? false : 'position'}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`relative z-20 ${hasSearched ? 'mt-2' : ''}`}>

            <form onSubmit={handleSearch} className="px-2 py-2 md:p-6">
              <div className="flex flex-col gap-3">
                {/* Trip Mode Toggle */}
                <div className="flex flex-nowrap gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => setTripMode('flight')}
                    className={`flex-1 md:flex-none md:px-5 py-1.5 rounded-2xl font-normal text-sm transition-all duration-300 ${
                      tripMode === 'flight' || tripMode === 'flight_stay'
                        ? 'bg-white text-black'
                        : pillGlassClass
                    }`}
                  >
                    Flights
                  </button>
                  <button
                    type="button"
                    onClick={() => !isChangeFlight && setTripMode(tripMode === 'flight_stay' ? 'flight' : 'flight_stay')}
                    disabled={isChangeFlight}
                    className={`flex-1 md:flex-none md:px-5 py-1.5 rounded-2xl font-normal text-sm transition-all duration-300 ${
                      tripMode === 'flight_stay'
                        ? 'bg-white text-black'
                        : pillGlassClass
                    } ${isChangeFlight ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    Stays
                  </button>
                  <button
                    type="button"
                    onClick={() => !isChangeFlight && navigate('/explore')}
                    disabled={isChangeFlight}
                    className={`flex-1 md:flex-none md:px-5 py-1.5 rounded-2xl font-normal text-sm transition-all duration-300 ${pillGlassClass} ${isChangeFlight ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    Explore
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <AirportInput
                    placeholder="From"
                    value={origin}
                    onChange={setOrigin}
                    disabled={isChangeFlight}
                    inputClassName={`${pillGlassClass} text-white placeholder:text-white/60 rounded-2xl pl-12 pr-4 py-2 text-base font-normal h-[42px]`}
                    iconClassName="left-4 w-5 h-5 text-white/60 z-10"
                  />
                  <AirportInput
                    placeholder="To"
                    value={destination}
                    onChange={setDestination}
                    disabled={isChangeFlight}
                    inputClassName={`${pillGlassClass} text-white placeholder:text-white/60 rounded-2xl pl-12 pr-4 py-2 text-base font-normal h-[42px]`}
                    iconClassName="left-4 w-5 h-5 text-white/60 z-10"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div className="relative">
                    {isMobile ?
                    <>
                        <Button type="button" variant="ghost" onClick={() => setIsCalendarOpen(true)} className={`relative w-full h-[42px] justify-start text-left font-normal rounded-2xl pl-12 pr-4 py-2 ${pillGlassClass}`}>
                          <CalendarIcon className="absolute left-4 w-5 h-5 text-white/60" />
                          {date?.from ? date.to ? `${formatDate(date.from, "MMM d") || 'Date'} - ${formatDate(date.to, "MMM d") || 'Date'}` : formatDate(date.from, "MMM d") || 'Date' : <span className="text-white/40">Dates</span>}
                        </Button>
                        {isCalendarOpen &&
                      <div className="fixed inset-0 z-[999] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-brand-950/70 backdrop-blur-sm md:hidden" onClick={() => setIsCalendarOpen(false)}>
                            <div className="rounded-t-2xl sm:rounded-2xl overflow-hidden w-full sm:max-w-sm border border-brand-800/45 bg-brand-950/95 backdrop-blur-2xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.8)] pb-[env(safe-area-inset-bottom)]" onClick={(e) => e.stopPropagation()}>
                              <CalendarRangeStyles />
                              <CalendarWithPrices initialFocus mode="range" defaultMonth={date?.from || (isChangeFlight && minChangeDepartureDate ? minChangeDepartureDate : getMinBookableDate())} selected={date} onSelect={handleDateSelect} onMonthChange={(m) => setCalendarViewMonth(new Date(Date.UTC(m.getFullYear(), m.getMonth(), 1)))} numberOfMonths={1} disabled={[{ before: isChangeFlight && minChangeDepartureDate ? minChangeDepartureDate : (isChangeFlight && isLoadingChangeFloor && !minChangeDepartureDate ? new Date(8640000000000000) : new Date(new Date().setDate(new Date().getDate() + 22))) }]} dayPrices={calendarPrices} lowestPrice={calendarLowestPrice} isLoading={calendarPricesLoading} />
                              <CalendarDock onDone={handleCalendarDone} note={isChangeFlight && minChangeDepartureDate
                                ? <p className="text-[11px] font-normal text-white/45">Must depart at least 5 days after your final installment ({minChangeDepartureDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} or later).</p>
                                : isChangeFlight && isLoadingChangeFloor
                                  ? <p className="text-[11px] font-normal text-white/45">Loading your installment schedule…</p>
                                  : null} />
                            </div>
                          </div>
                      }
                      </> :

                      <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                        <PopoverTrigger asChild>
                          <Button type="button" variant="ghost" className={`relative w-full h-[42px] justify-start text-left font-normal rounded-2xl pl-12 pr-4 py-2 overflow-hidden ${pillGlassClass}`}>
                            <CalendarIcon className="absolute left-4 w-5 h-5 text-white/60" />
                            {/* No year — "Aug 15 - Aug 22, 2026" overflowed the pill's
                                right edge; the calendar popover shows the year anyway.
                                `truncate` is the safety net for long month pairs. */}
                            <span className="truncate">
                              {date?.from ? date.to ? `${formatDate(date.from, "MMM d") || 'Date'} - ${formatDate(date.to, "MMM d") || 'Date'}` : formatDate(date.from, "MMM d") || 'Date' : <span className="text-white/40">Dates</span>}
                            </span>
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 rounded-2xl bg-brand-950/95 border-brand-800/45 text-white z-[99] backdrop-blur-xl shadow-2xl" align="start" side="bottom" sideOffset={8}>
                          <CalendarRangeStyles />
                          <CalendarWithPrices initialFocus mode="range" defaultMonth={date?.from || (isChangeFlight && minChangeDepartureDate ? minChangeDepartureDate : getMinBookableDate())} selected={date} onSelect={handleDateSelect} onMonthChange={(m) => setCalendarViewMonth(new Date(Date.UTC(m.getFullYear(), m.getMonth(), 1)))} numberOfMonths={numberOfMonths} disabled={[{ before: isChangeFlight && minChangeDepartureDate ? minChangeDepartureDate : (isChangeFlight && isLoadingChangeFloor && !minChangeDepartureDate ? new Date(8640000000000000) : new Date(new Date().setDate(new Date().getDate() + 22))) }]} dayPrices={calendarPrices} lowestPrice={calendarLowestPrice} isLoading={calendarPricesLoading} />
                          <CalendarDock onDone={handleCalendarDone} note={isChangeFlight && minChangeDepartureDate
                                ? <p className="text-[11px] font-normal text-white/45">Must depart at least 5 days after your final installment ({minChangeDepartureDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} or later).</p>
                                : isChangeFlight && isLoadingChangeFloor
                                  ? <p className="text-[11px] font-normal text-white/45">Loading your installment schedule…</p>
                                  : null} />
                        </PopoverContent>
                      </Popover>
                    }
                  </div>
                  
                  <div className="hidden md:block">
                    <PassengerSelector passengers={passengers} onChange={setPassengers} onOpenChange={onTravelersOpenChange} disabled={isChangeFlight} className={`${pillGlassClass} rounded-2xl px-4 py-2 h-[42px] justify-start text-base font-normal`} />
                  </div>
                  <div className="hidden md:block">
                    <CabinClassSelector value={cabinClass} onChange={setCabinClass} className={`${pillGlassClass} rounded-2xl px-4 py-2 h-[42px] justify-start text-base font-normal`} />
                  </div>
                  <div className="hidden md:block">
                    <PaymentMethodSelector value={paymentMethod} onChange={setPaymentMethod} milesBalance={milesBalance} reserveBalanceCents={reserveBalanceCents} hasStay={tripMode === 'flight_stay'} disabled={isChangeFlight} className={`${pillGlassClass} rounded-2xl px-4 py-2 h-[42px] justify-start text-base font-normal`} />
                  </div>
                </div>
                
                {/* Travelers takes its own width (a fixed 15% was narrower than
                    the pill, which then ran into Economy); cabin and payment
                    share the rest. */}
                <div className="flex gap-2.5 max-[374px]:gap-2 md:hidden">
                  <div className="flex-none">
                    <PassengerSelector passengers={passengers} onChange={setPassengers} onOpenChange={onTravelersOpenChange} disabled={isChangeFlight} className={`${pillGlassClass} rounded-2xl px-4 py-2 h-[42px] justify-start text-base font-normal`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <CabinClassSelector value={cabinClass} onChange={setCabinClass} className={`${pillGlassClass} rounded-2xl px-4 py-2 h-[42px] justify-start text-base font-normal`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <PaymentMethodSelector value={paymentMethod} onChange={setPaymentMethod} milesBalance={milesBalance} reserveBalanceCents={reserveBalanceCents} hasStay={tripMode === 'flight_stay'} disabled={isChangeFlight} className={`${pillGlassClass} rounded-2xl px-4 py-2 h-[42px] justify-start text-base font-normal`} />
                  </div>
                </div>
                
                {/* Submit row — on desktop the sort/filter controls sit inline
                    far-left (only once results exist) with Search far-right;
                    on mobile Search stays a full-width CTA and the filters
                    render in their own row below the form (md:hidden there). */}
                <div className="mt-3 flex items-center gap-3">
                  {hasSearched && !isLoading && !selectedOutboundFlight && rawUniqueOutboundFlights.length > 0 && (
                    <div className="hidden md:flex min-w-0">
                      <FilterSort
                        sortBy={sortBy}
                        setSortBy={setSortBy}
                        stopsFilter={stopsFilter}
                        setStopsFilter={setStopsFilter}
                        airlineFilter={airlineFilter}
                        setAirlineFilter={setAirlineFilter}
                        uniqueAirlines={uniqueAirlines}
                        onReset={resetFilters}
                        disableAirlines={isChangeFlight} />
                    </div>
                  )}
                  {/* A full-width 38px CTA on mobile, under the 42px fields
                      (founder, 10-22: less tall, twice; it was ~49px from
                      py-6); desktop is h-10 so it sits balanced beside the
                      h-8 filter pills instead of towering over them. */}
                  <Button type="submit" variant="ghost" className={`w-full md:w-auto md:ml-auto md:min-w-[220px] h-[38px] py-0 md:h-10 rounded-full font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 ${pillGlassClass}`} disabled={isLoading}>
                    <SearchIcon className="w-4 h-4 mr-2" />
                    Search
                  </Button>
                </div>
              </div>
            </form>

            {/* Not in the iOS app: the names and places are generated, not real searches. */}
            {!hasSearched && !isNativeApp && (
              <div className="mt-6 md:mt-8 flex justify-center">
                <div className="bg-white/5 backdrop-blur-[3px] border border-white/10 rounded-full px-4 py-2 inline-flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={currentSearchIndex}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3 }}
                      className="text-white/70 text-xs md:text-sm font-normal"
                    >
                      {searches[currentSearchIndex]}
                    </motion.p>
                  </AnimatePresence>
                </div>
              </div>
            )}

            {hasSearched &&
            <div className="pt-3 relative z-10" id="search-results">
                {error && !isLoading &&
              <motion.div className="glass bg-red-900/20 border-red-500/30 rounded-xl p-4 text-center text-red-300" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <p>{error}</p>
                  </motion.div>
              }
                
                {!isLoading &&
              <>
                    {/* Phones: how fast this search was (fast searches only)
                        and the Grand total switch, on one centered line; on a
                        screen too narrow for both, the switch wraps under.
                        Desktop shows both in the heading row below. */}
                    {!selectedOutboundFlight &&
                <div className="mb-2.5 flex flex-wrap items-center justify-center gap-1.5 md:hidden">
                        {showSpeedStat && <SearchSpeedStat ms={searchSpeed.ms} count={rawUniqueOutboundFlights.length} complete={searchSpeed.complete} compact />}
                        <AllInPriceToggle compact checked={showAllInPrices} onCheckedChange={setShowAllInPrices} />
                      </div>
                }

                    {/* Only show filters if there are initial raw unique outbound
                        flights. Mobile-only — on desktop the same controls render
                        inline beside the Search button in the form's submit row. */}
                    {!selectedOutboundFlight && rawUniqueOutboundFlights.length > 0 &&
                <div className="mb-4 flex md:hidden flex-row items-center justify-center gap-4 flex-wrap">
                        <FilterSort
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    stopsFilter={stopsFilter}
                    setStopsFilter={setStopsFilter}
                    airlineFilter={airlineFilter}
                    setAirlineFilter={setAirlineFilter}
                    uniqueAirlines={uniqueAirlines}
                    onReset={resetFilters}
                    disableAirlines={isChangeFlight} />

                      </div>
                }

                    {/* Cheaper-alternatives price-comparison card. Its home is
                        BETWEEN the Best flights block and Other flights (below);
                        this top slot is the fallback for views without a Best
                        section — an explicit sort, or a search where nothing
                        carries Google's best tag — so the callout never
                        disappears along with the section. */}
                    {bestOutboundFlights.length === 0 && cheaperAlternativesCard}

                    <div className="space-y-4 mt-1">
                      <div className="flex flex-wrap justify-between items-center gap-2">
                        <h3 className="text-lg font-medium text-white">
                          {selectedOutboundFlight ? 'Selected Outbound Flight' : 'Select an outbound flight'}
                        </h3>
                        {showSpeedStat &&
                    <div className="hidden md:flex">
                            <SearchSpeedStat ms={searchSpeed.ms} count={rawUniqueOutboundFlights.length} complete={searchSpeed.complete} />
                          </div>
                    }
                        {selectedOutboundFlight ? (
                          <Button variant="link" onClick={handleDeselectOutbound} className="text-primary hover:text-primary-foreground px-0">
                            Change Flight
                          </Button>
                        ) : (
                          // Desktop only; phones have the compact one beside the speed stat.
                          <AllInPriceToggle checked={showAllInPrices} onCheckedChange={setShowAllInPrices} className="hidden md:inline-flex" />
                        )}
                      </div>
                      {/* Google's ranked picks, pinned like Google Flights'
                          "Best departing flights" block. Renders only while
                          choosing an outbound under the default 'best' sort
                          (bestOutboundFlights is [] otherwise). */}
                      {!selectedOutboundFlight && !showReturnFlights && bestOutboundFlights.length > 0 &&
                  <>
                        <div className="flex items-baseline gap-2.5">
                          <span className="text-sm font-medium text-white/85">Best flights</span>
                          <span className="text-[11px] font-normal text-white/35">ranked by price, duration & stops</span>
                        </div>
                        <AnimatePresence>
                          {bestOutboundFlights.map((offer) => <FlightOfferCard showAllInPrices={showAllInPrices} key={offer.id} offer={offer} onFlightSelect={handleFlightSelection} paymentMethod={paymentMethod} milesBalance={milesBalance} reserveBalanceCents={reserveBalanceCents} hideBaggageIcons={true} reserveSwitchContext={reserveSwitchTrip ? { reserveSwitchTrip, reservedBaseFareCents: reserveSwitchTrip.base_fare_cents || 0 } : null} changeFlightContext={changeFlightTrip ? { changeFlightTrip, originalBaseCents: changeFlightOriginalBaseCents } : null} />)}
                        </AnimatePresence>
                        {cheaperAlternativesCard && <div className="pt-2">{cheaperAlternativesCard}</div>}
                        <div className="flex items-baseline gap-2.5 pt-3">
                          <span className="text-sm font-medium text-white/85">Other flights</span>
                        </div>
                      </>
                  }
                      <AnimatePresence>
                        {selectedOutboundFlight && showReturnFlights ?
                    <FlightOfferCard showAllInPrices={showAllInPrices} key={`${selectedOutboundFlight.id}-selected`} offer={selectedOutboundFlight} paymentMethod={paymentMethod} milesBalance={milesBalance} reserveBalanceCents={reserveBalanceCents} hideBaggageIcons={true} reserveSwitchContext={reserveSwitchTrip ? { reserveSwitchTrip, reservedBaseFareCents: reserveSwitchTrip.base_fare_cents || 0 } : null} changeFlightContext={changeFlightTrip ? { changeFlightTrip, originalBaseCents: changeFlightOriginalBaseCents } : null} /> :

                    offers.map((offer) => <FlightOfferCard showAllInPrices={showAllInPrices} key={offer.id} offer={offer} onFlightSelect={handleFlightSelection} paymentMethod={paymentMethod} milesBalance={milesBalance} reserveBalanceCents={reserveBalanceCents} hideBaggageIcons={true} reserveSwitchContext={reserveSwitchTrip ? { reserveSwitchTrip, reservedBaseFareCents: reserveSwitchTrip.base_fare_cents || 0 } : null} changeFlightContext={changeFlightTrip ? { changeFlightTrip, originalBaseCents: changeFlightOriginalBaseCents } : null} />)
                    }
                      </AnimatePresence>

                      {!selectedOutboundFlight && !showReturnFlights && hiddenOutboundCount > 0 &&
                  <div className="flex justify-center pt-4">
                          <Button type="button" variant="ghost" onClick={handleShowMore} className={`px-8 h-11 rounded-full font-medium text-white transition-all duration-300 ${pillGlassClass}`}>
                            Show more
                          </Button>
                        </div>
                  }
                    </div>

                    {showReturnFlights && returnFlightOffers.length > 0 &&
                <div className="space-y-4 mt-6" id="return-flights">
                        <div className="border-t border-white/10 pt-6 space-y-4">
                          <h3 className="text-lg font-medium text-white">Select a return flight</h3>
                          {/* Google's ranked picks for the return leg, pinned like
                              the outbound "Best flights" block. Header pair only
                              renders when Google tagged best picks for this leg —
                              otherwise the whole list is one price-sorted section,
                              same as the outbound fallback. */}
                          {bestReturnFlights.length > 0 &&
                    <>
                          <div className="flex items-baseline gap-2.5">
                            <span className="text-sm font-medium text-white/85">Best flights</span>
                            <span className="text-[11px] font-normal text-white/35">ranked by price, duration & stops</span>
                          </div>
                          <AnimatePresence>
                            {bestReturnFlights.map((offer) => <FlightOfferCard showAllInPrices={showAllInPrices} key={`return-${offer.id}`} offer={offer} isReturnJourney={true} onFlightSelect={handleReturnFlightSelection} paymentMethod={paymentMethod} milesBalance={milesBalance} reserveBalanceCents={reserveBalanceCents} hideBaggageIcons={true} outboundDepartureDateOverride={selectedOutboundFlight?.slices?.[0]?.segments?.[0]?.departing_at} reserveSwitchContext={reserveSwitchTrip ? { reserveSwitchTrip, reservedBaseFareCents: reserveSwitchTrip.base_fare_cents || 0 } : null} changeFlightContext={changeFlightTrip ? { changeFlightTrip, originalBaseCents: changeFlightOriginalBaseCents, selectedOutboundFlight } : null} />)}
                          </AnimatePresence>
                          <div className="flex items-baseline gap-2.5 pt-3">
                            <span className="text-sm font-medium text-white/85">Other flights</span>
                          </div>
                        </>
                    }
                          <AnimatePresence>
                            {visibleReturnFlights.map((offer) => <FlightOfferCard showAllInPrices={showAllInPrices} key={`return-${offer.id}`} offer={offer} isReturnJourney={true} onFlightSelect={handleReturnFlightSelection} paymentMethod={paymentMethod} milesBalance={milesBalance} reserveBalanceCents={reserveBalanceCents} hideBaggageIcons={true} outboundDepartureDateOverride={selectedOutboundFlight?.slices?.[0]?.segments?.[0]?.departing_at} reserveSwitchContext={reserveSwitchTrip ? { reserveSwitchTrip, reservedBaseFareCents: reserveSwitchTrip.base_fare_cents || 0 } : null} changeFlightContext={changeFlightTrip ? { changeFlightTrip, originalBaseCents: changeFlightOriginalBaseCents, selectedOutboundFlight } : null} />)}
                          </AnimatePresence>
                          {hiddenReturnCount > 0 &&
                    <div className="flex justify-center pt-4">
                            <Button type="button" variant="ghost" onClick={() => setReturnVisibleCount((count) => count + itemsPerPage)} className={`px-8 h-11 rounded-full font-medium text-white transition-all duration-300 ${pillGlassClass}`}>
                              Show more
                            </Button>
                          </div>
                    }
                        </div>
                      </div>
                }

                    {showReturnFlights && returnFlightOffers.length === 0 && !error &&
                <div className="text-center py-16 glass rounded-2xl mt-12">
                        <div className="w-16 h-16 mx-auto mb-6 glass-light rounded-2xl flex items-center justify-center">
                          <SearchIcon className="w-8 h-8" style={{ color: 'var(--text-muted)' }} />
                        </div>
                        <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                          {paymentMethod === 'defera_zero' ? 'No Return Flights Found' : 'No Return Flights Found'}
                        </h3>
                        <p className="mb-6 max-w-xs mx-auto text-sm" style={{ color: 'var(--text-secondary)' }}>
                          {paymentMethod === 'defera_zero'
                            ? 'Defera Zero shows flights from United, American, Southwest, JetBlue, and Virgin Atlantic only. No return options for this outbound. Try a different outbound flight.'
                            : 'We couldn\'t find any return flights for the selected outbound journey. Please try selecting a different outbound flight.'}
                        </p>
                        <Button variant="link" onClick={handleDeselectOutbound} className="text-primary hover:text-primary-foreground">
                          Go Back to Outbound Selection
                        </Button>
                      </div>
                }

                    {!isLoading && !selectedOutboundFlight && processedFlights.length === 0 && rawUniqueOutboundFlights.length > 0 && !error &&
                <div className="text-center py-16 glass rounded-2xl">
                        <div className="w-16 h-16 mx-auto mb-6 glass-light rounded-2xl flex items-center justify-center">
                          <SearchIcon className="w-8 h-8" style={{ color: 'var(--text-muted)' }} />
                        </div>
                        <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                          {paymentMethod === 'defera_zero' ? 'No Flights Found' : 'No Matching Flights Found'}
                        </h3>
                        <p className="mb-6 max-w-xs mx-auto text-sm" style={{ color: 'var(--text-secondary)' }}>
                          {paymentMethod === 'defera_zero'
                            ? 'Defera Zero shows flights from United, American, Southwest, JetBlue, and Virgin Atlantic only. No options available for this search.'
                            : 'Your current filter settings are too restrictive. Try adjusting them.'}
                        </p>
                        <Button variant="link" onClick={resetFilters} className="text-primary hover:text-primary-foreground">
                          Reset Filters
                        </Button>
                      </div>
                }

                    {!isLoading && !selectedOutboundFlight && rawUniqueOutboundFlights.length === 0 && !error &&
                <div className="text-center py-16 glass rounded-2xl">
                        <div className="w-16 h-16 mx-auto mb-6 glass-light rounded-2xl flex items-center justify-center">
                          <SearchIcon className="w-8 h-8" style={{ color: 'var(--text-muted)' }} />
                        </div>
                        <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>No Flights Found</h3>
                        <p className="mb-6 max-w-xs mx-auto text-sm" style={{ color: 'var(--text-secondary)' }}>
                          We couldn't find any flights for this route. Try adjusting your search criteria.
                        </p>
                      </div>
                }
                  </>
              }
              </div>
            }
          </motion.div>
        </div>
      </div>
    </div>
    </div>
    </>
  );

}